/**
 * Application state, offline queue and all the actions the views call.
 *
 * The store is offline-first on purpose: the mobile shells must stay usable on a
 * train. Every mutation is applied to the local snapshot immediately, appended
 * to a durable queue, and replayed against the API as soon as it answers again.
 * The server stays authoritative — once the queue drains, anything that changed
 * more than the row it returned triggers a refetch.
 */

import { APP_VERSION } from './version.js';
import { api, setToken, getToken, ApiError, OfflineError, siteUrl, sealForPreview } from './api.js';
import { returnUrl, openAuthorizeUrl, takeCallbackParams, onDeepLink, isNativeShell } from './oauth.js';
import { computeMetrics, tradesFingerprint } from '../../shared/compute.js';
import { priceRegion, rememberRegion } from '../../shared/price-region.js';
import { localDay,
  currentMonth, shiftMonth, DEFAULT_SETTINGS, rateRowForMonth, resolveRates, rebaseRateHistory, normaliseCurrency,
  milesToKm, gallonsToLitres, kmToMiles, litresToGallons, TABS,
  recurringDue, recurringPosted, recurringFallsIn, monthlyEquivalent, nextChargeMonth, nextChargeDate, daysBetween, isMonth,
  debtHistory, debtLatestBalance, debtHistoryWithBalance, debtHistoryWithPayment,
  debtHistoryWithoutPayment, debtOwedIn, CATEGORIES, homeCurrency, homeUnits, isTaxableTrade
} from '../../shared/model.js';
import { countryOfTimeZone } from '../../shared/timezones.js';
import {
  readCsv, findHeaderRow, guessColumns, rowsToEntries, toCsv
} from '../../shared/csv.js';
import { readBrokerTrades, typedTwins } from '../../shared/broker-trades.js';
import {
  classify, merchantKey, payeeOf, cleanDescription, TRANSFER
} from '../../shared/merchants.js';

const CACHE_KEY = 'pt.snapshot';
const QUEUE_KEY = 'pt.queue';
const UI_KEY = 'pt.ui';
const AD_CONSENT_KEY = 'pt.adConsent';

/** Ops whose local effect is wider than the row the API hands back. */
const NEEDS_REFETCH = new Set([
  'payDebt', 'addFuel', 'deleteFuel', 'addCarExpense', 'deleteCarExpense', 'updateHolding',
  /*
   * An edited debt comes back normalised -- a rounded term, an upper-cased
   * currency -- and the local guess writes what was typed. Small divergences,
   * but they sit on screen until something else forces a read.
   */
  'updateDebt',
  /* Same: the server normalises a currency code and rounds where it must,
     and a corrected ledger line can move to a different month entirely. */
  'updateIncome', 'updateExpense',
  /*
   * Both of these move a mirrored ledger line as well as the row they name, so
   * the local guess is narrower than what the server actually changed.
   */
  'updateFuel', 'updateCarExpense',
  // A rate change re-values the whole ledger, not just the row it touched.
  'setRate', 'deleteRate',
  // A rate here re-values every cash position in that currency, not one row.
  'setInflation', 'deleteInflation'
]);

const readJson = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

/*
 * The landing page's preview (see `startPreview`). It shares this origin's
 * storage with the real app, so while it runs nothing is written there: the
 * cached ledger, the queue and the UI state all belong to whoever uses the app
 * in this browser.
 */
let sandbox = false;

const writeJson = (key, value) => {
  if (sandbox) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or disabled — the session simply stops being resumable.
  }
};

const tempId = () => 'tmp_' + Math.random().toString(36).slice(2, 10);

/** A date input hands back '' when empty and 'YYYY-MM-DD' otherwise. */
const isDate = value => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
const isTemp = id => typeof id === 'string' && id.startsWith('tmp_');

const ui = readJson(UI_KEY, {});

/*
 * A browser that has never held a session here -- nothing remembered, no
 * cached ledger -- belongs to somebody who has no account yet, so the screen
 * opens on "Create an account" rather than asking them to sign in.
 */
function firstAuthMode() {
  if (Object.keys(ui).length) return 'signIn';
  try {
    if (window.localStorage.getItem(CACHE_KEY)) return 'signIn';
  } catch {
    /* Storage off: nothing is remembered either way. */
  }
  return 'signUp';
}

/**
 * The language to greet somebody in before any account says otherwise.
 *
 * This device's own choice first; then the one made on the landing page, which
 * shares this origin and is where most people arrive from; then the browser's
 * language. The default used to be English regardless, so a Polish visitor who
 * pressed "Otwórz Portfolio Tracker" on the Polish page met an English sign-in.
 */
function firstLanguage() {
  if (ui.lang) return ui.lang;
  try {
    const landing = window.localStorage.getItem('lr_portfolio_lang');
    if (landing === 'PL' || landing === 'EN') return landing;
  } catch {
    /* Storage can be off; the browser still knows its language. */
  }
  const browser = typeof navigator !== 'undefined' ? String(navigator.language || '') : '';
  return browser.toLowerCase().startsWith('pl') ? 'PL' : DEFAULT_SETTINGS.language;
}

/**
 * Capacitor injects `window.Capacitor` in the native shells; in a plain browser
 * we fall back to the user agent, which is enough for the two chrome details
 * that actually differ (safe-area padding and the FAB's corner radius).
 */
function detectPlatform() {
  const native = window.Capacitor?.getPlatform?.();
  if (native === 'ios' || native === 'android') return native;

  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return 'web';
}

const state = {
  status: getToken() ? 'loading' : 'anon',
  user: null,
  data: null,
  metrics: null,
  /* The metrics for the calendar month, for projections that start from today (see recompute). */
  metricsNow: null,
  /* The prices shown and sold: { region, currency } -- see shared/price-region.js. */
  priceRegion: priceRegion(),
  settings: { ...DEFAULT_SETTINGS },
  tab: ui.tab || 'overview',
  /** -1 back, 1 forward, 0 neither: which way the next page slides in. */
  tabFrom: 0,
  month: ui.month || currentMonth(),
  lang: firstLanguage(),
  sheet: null,
  /** 'web' | 'ios' | 'android' — only affects chrome (safe areas, FAB shape). */
  platform: detectPlatform(),
  online: navigator.onLine !== false,
  syncing: false,
  pending: 0,
  /*
   * Whether sync activity has lasted long enough to be worth showing. See
   * `trackSyncVisibility` -- the counts above stay truthful, this one waits.
   */
  syncVisible: false,
  busy: false,
  error: null,
  notice: null,
  authMode: firstAuthMode(),
  claimOpen: false,
  /* The terms box on whichever form is asking; it starts unticked every time. */
  termsTicked: false,
  /* The site the app belongs to, where the terms and the privacy policy live. */
  siteBase: siteUrl('/'),
  /* Whether the delete-account confirmation is open in the preferences. */
  deleteOpen: false,
  settingsOpen: false,
  /* Which Preferences section is open; null is the list of them on a phone (views/settings.js). */
  settingsSection: null,
  /* Currencies added in Preferences that have no rate yet: a row to type one into. */
  settingsAddedCodes: [],
  /* "✓ saved" in the Preferences header, for a moment after a change. */
  settingsSaved: false,
  versionCopied: false,
  /* The sidebar's account menu (Preferences, Admin, Sign out). */
  accountMenuOpen: false,
  /* The question asked before the month's fixed costs are added a second time. */
  recurringAgain: false,
  /* The admin panel: open or not, what it loaded, and the account search. */
  adminOpen: false,
  admin: null,
  adminQuery: '',
  /** Whether the password form in Preferences is unfolded. */
  passwordOpen: false,
  /*
   * Which month picker is open, and the year it is showing. The native
   * <input type="month"> was a system control dropped into a designed
   * interface — right in behaviour, wrong in every other respect.
   */
  monthPicker: null,
  monthPickerYear: null,
  /*
   * Dates chosen in the app's own calendar, held here because the picker is a
   * button rather than an <input> and so has nowhere of its own to keep them.
   * Empty means today, which is what these forms always silently assumed.
   */
  fuelDate: '',
  serviceDate: '',
  /** The fill being entered did not fill the tank -- see shared/compute.js. */
  fuelPartial: false,
  /** The full-tank flag as changed in the fill-up dialog; null until touched. */
  editFuelFull: null,

  tradeDate: '',
  /* The journal's currency: the one used last on this device, dollars until then. */
  tradeCurrency: ui.tradeCurrency || 'USD',
  /*
   * The journal's two halves: trades really taken, and backtesting sessions --
   * a strategy replayed on past charts. Which half is open, and which session,
   * is remembered on this device.
   */
  /* 'pit' was a fourth mode until PIT-38 moved to the Taxes tab; it falls back to the journal. */
  journalMode: ['backtest', 'prop'].includes(ui.journalMode) ? ui.journalMode : 'live',
  /*
   * PIT-38: the tax year shown (last year until somebody picks another), what
   * was typed for each year -- kept on this device only -- and the journal's
   * trades as last converted at NBP rates.
   */
  /*
   * Last year's return while it can still be filed (to 30 April), this year's
   * after: in October the year that matters is the one still being traded.
   */
  pitYear: ui.pitYear || (new Date().getMonth() < 4 ? new Date().getFullYear() - 1 : new Date().getFullYear()),
  pitInputs: ui.pitInputs && typeof ui.pitInputs === 'object' ? ui.pitInputs : {},
  /*
   * Taxes: whose return (see TAX_COUNTRIES in views/taxes.js) -- Poland for a
   * Polish reader, the USA otherwise, until somebody picks -- and what was
   * typed into the US form, per year. The year itself is `pitYear`, shared.
   */
  taxCountry: typeof ui.taxCountry === 'string' ? ui.taxCountry : null,
  /*
   * What was typed for every country but Poland (whose figures are
   * `pitInputs`), as { country: { year: { field: value } } }. The US form's
   * first day kept its own key, `taxUs`; it moves in here.
   */
  taxInputs: (() => {
    const saved = ui.taxInputs && typeof ui.taxInputs === 'object' ? ui.taxInputs : {};
    return !saved.us && ui.taxUs && typeof ui.taxUs === 'object' ? { ...saved, us: ui.taxUs } : saved;
  })(),
  /* The tax summaries this account asked for; null until fetched. */
  taxVotes: null,
  /* Which budget is being changed in place, and the goal form: open, which goal, its deadline. */
  budgetUi: { edit: null },
  goalUi: { open: false, edit: null, deadline: null, rev: 0 },
  pitConverted: null,
  pitLoading: false,
  /* Prop-firm accounts, loaded when that half of the journal is first opened. */
  prop: null,
  propLoading: false,
  /* The prop account whose delete is waiting for its confirmation. */
  propConfirm: null,
  /* The prop account whose rules are being changed. */
  propRulesEdit: null,
  /* Payment reminders on this device: what it supports, whether they are on, and what they cover. */
  push: { support: null },
  /*
   * The prop screen's open forms: a new account, a fee or payout on one
   * account (with its kind and whether it goes to the budget), and the
   * drawdown type picked in the rules being changed.
   */
  propUi: { newOpen: false, entryFor: null, kind: 'fee', toLedger: true, ddType: 'eod' },
  backtests: null,
  backtestId: ui.backtestId || null,
  /* The session delete waiting for its confirmation. */
  backtestConfirm: false,
  /* The "what if" simulator's sliders, remembered on this device. */
  whatIf: {
    extra: 0, invest: 0, ret: 7, years: 5,
    ...(ui.whatIf && typeof ui.whatIf === 'object' ? ui.whatIf : {})
  },
  /* The month under the pointer on the "what if" chart; null: none. */
  whatIfHover: null,
  /* The full simulator, opened from its card on the overview. */
  whatIfOpen: false,
  /* 1 when it was just opened, -1 when just closed, 0 otherwise: which way the swap animates. */
  whatIfFrom: 0,
  /* The month under a finger on the phone's net worth trend; null: this month. */
  trendHover: null,
  /* Every later fixed cost on the phone, not just the first three. */
  upcomingAll: false,
  /*
   * The income & spend tab's own view state: which list, which category it is
   * filtered to, which groups are folded, and the two inline forms. Not
   * persisted -- it describes a visit, not a preference.
   */
  /* Backtesting's view state: the session form, and the quick entry with what it keeps between trades. */
  backtestUi: {
    snOpen: false, snEdit: null, from: null, to: null, qOpen: false, qEdit: null, rev: 0, date: null,
    q: { side: 'LONG', tf: null, exit: null, unit: 'R', more: false }
  },
  /* The journal's view state: list filter, the calendar's period, open rows, the trade form. */
  journalUi: {
    filter: 'all', range: null, openId: null, riskOpen: false, formOpen: false, editId: null, rev: 0,
    date: null, d: {}, calMode: 'day', calMonth: null
  },
  /* The vehicle tab's view state: the two forms, and which kind of service is shown. */
  vehUi: {
    fuelOpen: false, fuelEdit: null, full: true, fuelDate: null,
    svcOpen: false, svcEdit: null, svcType: 'Maintenance', svcDate: null, filter: 'all', rev: 0
  },
  /* The investments tab's view state: which positions are open, and the purchase form. */
  invUi: {
    open: {}, formOpen: false, editId: null, cls: 'Equity / ETF', since: null, cur: null, rev: 0,
    /* Precious metals: paper or physical, which metal, and the unit it is weighed in. */
    metalForm: null, metal: null, unit: null,
    /* Fineness: a share from the list, 'other' for one typed in (purityTyped, per mille). */
    purity: null, purityTyped: null,
    /* What the metal cost in the purchase month, fetched for the form: { key, status, price, perOunce }. */
    metalQuote: null
  },
  /* The debt tab's view state: whose payment row is open, and the debt form. */
  debtUi: { payId: null, formOpen: false, editId: null, kind: 'loan', rev: 0 },
  /* The payoff plan's monthly overpayment, remembered on this device. */
  debtExtra: Number.isFinite(ui.debtExtra) ? ui.debtExtra : 0,
  cashUi: {
    tab: 'all', cat: null, closed: {}, adding: false, addKind: 'expense', addCat: 'Groceries',
    recurring: false, fxOpen: false, fxId: null, fxKind: 'expense', fxEvery: 1, fxCat: 'Other', fxRev: 0
  },
  /* Bumped when a preset moves the sliders, so the range inputs are re-made at the new values. */
  whatIfRev: 0,
  /* The weekly review, loaded when the overview first shows it. */
  review: null,
  reviewLoading: false,
  /* How many months of spending the savings cushion holds, before any goes to trading. */
  cushionMonths: [3, 6, 12].includes(ui.cushionMonths) ? ui.cushionMonths : 6,
  /* Trading on prop firm accounts: the firm's capital is at risk, not the savings, so no cushion. */
  propTrader: ui.propTrader === true,
  /** Which month the day grid is showing; null when only months are picked. */
  datePickerMonth: null,
  /*
   * The holding whose exit is being confirmed.
   *
   * The x used to delete the row outright, which erased the position from every
   * month it had ever been held in. Selling and never having owned it are
   * different things and now have to be told apart before anything happens.
   */
  holdingExit: null,
  /* Which single row is open for correction: { kind, id }. One field rather
     than a pair per kind, so two dialogs cannot be open at once. */
  editing: null,
  /* The class chosen inside a holding dialog, which changes its shape. */
  editClass: null,
  /* The server's own words, kept for the sign-in screen only. */
  errorDetail: null,
  /*
   * Which asset class the add-holding form is on. Held here because the form
   * changes shape for cash — no units, no average cost — and an uncontrolled
   * select cannot tell the renderer that on its own.
   */
  holdingClass: 'Equity / ETF',
  /** 'idle' | 'connecting' | 'done' | 'error' — mirrors the login panel design. */
  authPhase: 'idle',
  authErrorCode: null,
  discordEnabled: false,
  /** Set when sign-in was refused because the address is unconfirmed. */
  unverifiedEmail: null,
  /*
   * The token off a password reset link. Held only until it is spent: it is a
   * credential, so it is never written to localStorage or left in the URL.
   */
  resetToken: null,
  /*
   * Which currency the add-forms are currently entering in. Remembered per
   * device: somebody paid in euro is usually about to enter several.
   */
  entryCurrency: ui.entryCurrency || null,
  /** Set between a successful Discord exchange and the user pressing "enter". */
  pendingAccount: null,
  /*
   * The last deleted row, kept just long enough to put it back. Every x in this
   * app is otherwise final, and a ledger where one mis-tap silently loses a
   * record you might not miss for weeks has the wrong default.
   */
  undo: null,
  /*
   * A statement being reviewed before it is imported. The file is read in the
   * browser and never uploaded: what eventually reaches the server is the list
   * of rows the user looked at and approved, nothing else.
   */
  importing: null,
  /*
   * The trading journal, fetched on demand rather than with the ledger.
   *
   * Not part of the snapshot on purpose: it is not part of the ledger, nothing
   * in `computeMetrics` reads it, and loading a year of trades on every boot
   * would cost every user who never opens the tab.
   */
  trades: null,
  tradesLoading: false,
  /*
   * Whether the last attempt to load the journal failed.
   *
   * Load-on-render plus "null means not loaded yet" is a retry loop the moment
   * the request fails: the failure leaves `trades` null, the notify that
   * follows re-renders, the render asks again. Measured at 3499 requests in
   * two and a half seconds against a server answering 500 — a client quietly
   * attacking its own backend at exactly the moment it is least able to cope.
   *
   * The flag is what stops it. Clearing it is a deliberate act (the retry
   * button), not something a repaint can do by accident.
   */
  tradesFailed: false,
  /* A broker export read and waiting to be confirmed, then what the import did; see openTradeImport. */
  tradeImport: null,
  /*
   * Advertising, for the free tier. Null until the server has been asked —
   * nothing is rendered and nothing is fetched from an ad network on a guess.
   */
  ads: null,
  /*
   * 'yes' | 'no' | null. Null means unanswered, and while it is null the ad
   * frame is not loaded at all: a consent prompt that runs the tracking it is
   * asking about is not a consent prompt. Per device, because it is a browser
   * storage decision rather than an account one.
   */
  adConsent: readAdConsent(),
  /*
   * 'waiting' | 'loaded' | 'blocked'. An ad blocker, an empty fill or a dead
   * network all end the same way: the frame never loads. Leaving a labelled
   * empty rectangle on the page would look like a bug in the app, so the slot
   * removes itself instead.
   *
   * Not worked around. Blocking advertising is the reader's choice and evading
   * it by renaming the path would be a decision made against them.
   */
  adFrame: 'waiting'
};

/** The stored answer, or null when there is not one yet. */
function readAdConsent() {
  try {
    const value = window.localStorage.getItem(AD_CONSENT_KEY);
    return value === 'yes' || value === 'no' ? value : null;
  } catch {
    return null;
  }
}

/** The saved opening screen applies once per launch, not on every refetch. */
let openingScreenApplied = false;
/* The tab the cached copy opened on, until the server's settings arrive -- see loadSnapshot. */
let openedFromCache = null;

/*
 * Unsent changes, and whose they are.
 *
 * The stamp exists for one sequence, which cost real entries before it did:
 * enter something on a phone with no signal, change the account password on
 * another device, and the phone's next flush is refused. The client signs out
 * on a refused flush -- correctly, the session is over -- and used to empty the
 * queue as it went, so work that had been shown as recorded was gone with no
 * trace and nothing said.
 *
 * Kept instead, and adopted again only by the same account. A queue belonging
 * to somebody else must never replay into the ledger of whoever signs in next,
 * which is the same rule the cached snapshot above follows.
 */
const readStoredQueue = () => {
  const stored = readJson(QUEUE_KEY, []);
  /* The old shape was a bare array: no owner, and no way to tell whose it is,
     so it is honoured once for whoever is signed in and re-stamped on write. */
  if (Array.isArray(stored)) return { owner: null, ops: stored };
  return { owner: stored?.owner ?? null, ops: Array.isArray(stored?.ops) ? stored.ops : [] };
};

let queueOwner = readStoredQueue().owner;
let queue = readStoredQueue().ops;
state.pending = queue.length;

const listeners = new Set();

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * How long something must be unsent before it is worth saying so.
 *
 * A settings change -- switching language, for one -- is queued and flushed in
 * about forty milliseconds. Announcing that is not information, and it is not
 * free either: "1 change" is a whole extra line in the sync panel, so the panel
 * grew by 16px and shrank again within one blink. The panels below sit at the
 * foot of a `flex: 1` spacer, which turned that into the language panel
 * visibly jumping 16px up and back on every click.
 *
 * Long enough that an ordinary round trip never shows; short enough that a real
 * backlog -- a dead connection, a queue that is not draining -- still announces
 * itself before anyone wonders whether their edit was saved.
 */
const SYNC_ANNOUNCE_MS = 260;

let syncAnnounceTimer = null;

/**
 * Gates the sync panel's transient states behind {@link SYNC_ANNOUNCE_MS}.
 *
 * `pending` and `syncing` stay truthful -- other logic depends on them and must
 * see the queue as it is. Only `syncVisible` is delayed, and only the wording
 * and the extra line read from it, so nothing about what is stored changes;
 * what changes is whether a state that lasted 40ms gets to repaint the sidebar.
 */
function trackSyncVisibility() {
  const busy = state.syncing || state.pending > 0;

  if (!busy) {
    if (syncAnnounceTimer) {
      clearTimeout(syncAnnounceTimer);
      syncAnnounceTimer = null;
    }
    // Mutated, not re-notified: this runs at the top of notify(), so the render
    // about to happen already picks it up.
    state.syncVisible = false;
    return;
  }

  if (state.syncVisible || syncAnnounceTimer) return;

  syncAnnounceTimer = setTimeout(() => {
    syncAnnounceTimer = null;
    // It may have drained while the timer ran; only announce what is still true.
    if (state.syncing || state.pending > 0) {
      state.syncVisible = true;
      notify();
    }
  }, SYNC_ANNOUNCE_MS);
}

/*
 * Confirmations that only say "done" -- saved, imported, prices updated --
 * clear themselves. They used to stay until dismissed, so "Vehicle updated"
 * followed the reader from tab to tab long after it meant anything. Errors and
 * notices that ask for something (an unsent e-mail, a held queue) still stay.
 */
const BRIEF_NOTICES = [
  'vehicle:saved', 'prices:ok', 'rates:ok', 'password:ok', 'recurring:none', 'ai:nothing', 'ai:allset',
  'prices:backfillNone', 'rates:backfillNone', 'recurring:posted:', 'import:done:', 'admin:saved:', 'ai:sorted:',
  'prices:backfilled:', 'rates:backfilled:', 'rates:auto:', 'push:on', 'push:off', 'push:test'
];
const NOTICE_MS = 8000;
let noticeShown = null;
let noticeTimer = null;

function scheduleNoticeClear() {
  if (state.notice === noticeShown) return;
  noticeShown = state.notice;
  clearTimeout(noticeTimer);
  noticeTimer = null;
  const notice = state.notice;
  if (!notice || !BRIEF_NOTICES.some(key => (key.endsWith(':') ? notice.startsWith(key) : notice === key))) return;
  noticeTimer = setTimeout(() => {
    noticeTimer = null;
    if (state.notice !== notice) return;
    state.notice = null;
    notify();
  }, NOTICE_MS);
}

function notify() {
  trackSyncVisibility();
  scheduleNoticeClear();
  listeners.forEach(fn => fn(state));
}

export function getState() {
  return state;
}

function persistUi() {
  writeJson(UI_KEY, {
    tab: state.tab, month: state.month, lang: state.lang,
    entryCurrency: state.entryCurrency,
    /* Written here or not at all: this object replaces what is stored. */
    tradeCurrency: state.tradeCurrency,
    journalMode: state.journalMode,
    cushionMonths: state.cushionMonths,
    propTrader: state.propTrader,
    whatIf: state.whatIf,
    debtExtra: state.debtExtra,
    pitYear: state.pitYear,
    pitInputs: state.pitInputs,
    taxCountry: state.taxCountry,
    taxInputs: state.taxInputs,
    backtestId: state.backtestId,
    /*
     * Kept with the rest: without it a reload forgot that the demo had been
     * used, and creating an account then defaulted to starting empty --
     * throwing away everything typed in the demo.
     */
    guestTouched: ui.guestTouched
  });
}

function persistQueue() {
  /* Stamped with the account it belongs to, so a later sign-in can tell
     whether it may replay it. */
  queueOwner = state.user?.id || queueOwner;
  writeJson(QUEUE_KEY, { owner: queueOwner, ops: queue });
  state.pending = queue.length;
}

/**
 * Takes up the stored queue, if it is this account's.
 *
 * Called once the signed-in account is known. Anything belonging to somebody
 * else is dropped rather than replayed -- one person's unsent expenses landing
 * in another's ledger is a far worse outcome than losing them.
 */
function adoptStoredQueue() {
  const stored = readStoredQueue();
  const mine = state.user?.id;
  if (!mine || !stored.ops.length) return;

  if (stored.owner && stored.owner !== mine) {
    queue = [];
    queueOwner = mine;
    persistQueue();
    return;
  }

  queue = stored.ops;
  queueOwner = mine;
  state.pending = queue.length;
  /* Said out loud: these were entered before, and are only now on their way. */
  if (queue.length) state.notice = 'queueRestored';
  notify();
}

/*
 * The cached ledger is stamped with whose it is. Without that stamp there is
 * no way to tell, on the next boot or the next sign-in, whether the figures on
 * screen belong to the person now looking at them.
 */
/*
 * A preference changed on this device goes into the cached ledger as well.
 * A reload restores settings from that cache before the server answers, and
 * it still held the old ones: a language switched a moment earlier came back
 * as the previous language -- on the sign-in screen too, when the session had
 * ended meanwhile.
 */
function rememberSettings(patch) {
  if (!state.data) return;
  state.data.settings = { ...(state.data.settings || {}), ...patch };
  persistSnapshot();
}

function persistSnapshot() {
  /*
   * No owner, no cache. A ledger written while the account is still unknown
   * would be indistinguishable from one belonging to whoever signs in next --
   * and an unowned cache is exactly what the checks below have to let through,
   * so the safe move is never to create one.
   */
  if (state.data && state.user?.id) {
    writeJson(CACHE_KEY, { user: state.user.id, data: state.data });
  }
}

function forgetSnapshot() {
  state.data = null;
  state.metrics = null;
  state.metricsNow = null;
  if (sandbox) return;
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch { /* nothing to clear */ }
}

function recompute() {
  const options = { strategy: state.settings.debtStrategy, unitSystem: state.settings.unitSystem };
  state.metrics = state.data ? computeMetrics(state.data, { ...options, month: state.month }) : null;
  /*
   * The same figures for the calendar month, for whatever looks ahead from
   * today. "What if" used to start from the month on screen, so picking
   * August projected five years from August's cash and debt -- a different
   * future for every month clicked.
   */
  const today = currentMonth();
  state.metricsNow = !state.data ? null
    : today === state.month ? state.metrics
      : computeMetrics(state.data, { ...options, month: today });
}

/** Applies a change to the local snapshot and refreshes everything derived from it. */
function local(mutator) {
  if (!state.data) return;
  mutator(state.data);
  recompute();
  persistSnapshot();
  scheduleAutoRates();
}

/*
 * A currency entered for the first time gets its rate without anybody asking.
 *
 * Until now an amount in euro on a złoty ledger was left out of every total --
 * "PLN 0", and a banner pointing at Preferences -- until a rate was fetched or
 * typed by hand. Now the rate is fetched as soon as the entry is made: today's
 * rate, then the monthly closes for the months the currency is used in. Once
 * per currency per session, so a provider that does not know a currency is not
 * asked again on every keystroke; a rate typed by hand is never overwritten.
 */
const autoRatesTried = new Set();
let autoRatesTimer = null;

function scheduleAutoRates() {
  if (autoRatesTimer) return;
  autoRatesTimer = setTimeout(() => {
    autoRatesTimer = null;
    autoFetchRates();
  }, 600);
}

/*
 * Currencies a view needs a rate for that no entry asked about -- the pound
 * for a UK tax summary of dollar trades, say. Fetched with the rest, once.
 */
const ratesAsked = new Set();

/** A view's own missing rates: fetched by themselves, as an entry's are. */
export function ensureRates(codes) {
  const base = state.settings?.baseCurrency;
  const fresh = (codes || []).map(code => String(code || '').toUpperCase())
    .filter(code => code && code !== base && !autoRatesTried.has(code) && !ratesAsked.has(code));
  if (!fresh.length) return;
  fresh.forEach(code => ratesAsked.add(code));
  scheduleAutoRates();
}

async function autoFetchRates() {
  if (sandbox || state.status !== 'ready' || !state.online || !state.user || state.user.isGuest) return;
  const wanted = [...new Set([...(state.metrics?.missingRates || []), ...ratesAsked])]
    .filter(code => code && !autoRatesTried.has(code));
  if (!wanted.length) return;
  wanted.forEach(code => autoRatesTried.add(code));
  /* The entry has to be on the server for its months to count -- send what is queued first. */
  await flush({ silent: true });
  try {
    const result = await api.refreshRates(wanted);
    const got = Object.keys(result?.rates || {}).filter(code => wanted.includes(code));
    if (!got.length) return;
    /* Past months get their own closing rate, as "FILL HISTORY" would; a failure here leaves today's. */
    await api.backfillRates(got).catch(() => null);
    await refresh({ silent: true });
    state.notice = 'rates:auto:' + got.join(', ');
    notify();
  } catch {
    /* Offline, or no provider: the banner still says what is missing, and Preferences can fetch it. */
  }
}

// ------------------------------------------------------------------ queue ---

function enqueue(op) {
  // The preview changes its own sample ledger and nothing else.
  if (sandbox) return;
  const last = queue[queue.length - 1];

  // Settings are last-write-wins, so a run of them collapses into one request.
  // Without this, toggling language while signed out (where `flush` cannot run)
  // grows the queue without bound.
  if (op.type === 'settings' && last && last.type === 'settings') {
    last.payload = { ...last.payload, ...op.payload };
    persistQueue();
    return;
  }

  queue.push({ id: tempId(), ...op });
  persistQueue();
}

/**
 * Drops a queued create that has not synced yet. Returns true when it found one,
 * meaning the row only ever existed locally and needs no delete request.
 */
function dropQueuedCreate(localId) {
  const before = queue.length;
  /*
   * While a flush runs, the op at the head of the queue is already on its way
   * to the server. Dropping it here would not recall it -- and the flush would
   * then shift the op behind it off the queue as if that one had been sent.
   * Left in place, it completes, its id is remapped, and a delete queued
   * behind it is rewritten to the real id.
   */
  queue = queue.filter((op, index) => op.tempId !== localId || (flushing && index === 0));
  if (queue.length !== before) {
    persistQueue();
    return true;
  }
  return false;
}

function remapId(collection, from, to) {
  local(data => {
    const row = (data[collection] || []).find(r => r.id === from);
    if (row) row.id = to;
  });
  // A delete queued before the create synced still references the temp id.
  queue.forEach(op => {
    if (op.targetId === from) op.targetId = to;
  });
  persistQueue();
  // So does the offer to take a payment back.
  if (state.undo?.row?.expenseId === from) state.undo.row.expenseId = to;
}

async function runOp(op) {
  /*
   * Creates carry the operation's id as an idempotency key: if the reply to an
   * earlier attempt was lost after the server saved the row, this attempt is
   * answered with that row instead of making a second one.
   */
  const once = { idempotencyKey: op.id };
  switch (op.type) {
    case 'addIncome': return api.addIncome(op.payload, once);
    case 'deleteIncome': return api.deleteIncome(op.targetId);
    case 'addExpense': return api.addExpense(op.payload, once);
    case 'deleteExpense': return api.deleteExpense(op.targetId);
    case 'addDebt': return api.addDebt(op.payload, once);
    case 'deleteDebt': return api.deleteDebt(op.targetId);
    /*
     * The row created locally is the Debt-service line, so that is the id to
     * remap it to; the payment's own id is of no use to the client.
     */
    case 'payDebt': return api.payDebt(op.targetId, op.payload, once)
      .then(result => ({ ...result, id: result?.expenseId }));
    case 'addHolding': return api.addHolding(op.payload, once);
    case 'deleteHolding': return api.deleteHolding(op.targetId);
    case 'addFuel': return api.addFuel(op.payload, once);
    case 'deleteFuel': return api.deleteFuel(op.targetId);
    case 'addCarExpense': return api.addCarExpense(op.payload, once);
    case 'deleteCarExpense': return api.deleteCarExpense(op.targetId);
    case 'updateVehicle': return api.updateVehicle(op.payload);
    case 'setCashBalance': return api.setCashBalance(op.payload.currency, op.payload.amount);
    case 'setRate': return api.setRate(op.payload.currency, op.payload.rate, op.payload.month);
    case 'setInflation': return api.setInflation(op.payload.currency, op.payload.rate);
    case 'deleteInflation': return api.deleteInflation(op.targetId);
    case 'updateHolding': return api.updateHolding(op.targetId, op.payload);
    case 'updateDebt': return api.updateDebt(op.targetId, op.payload);
    case 'updateIncome': return api.updateIncome(op.targetId, op.payload);
    case 'updateExpense': return api.updateExpense(op.targetId, op.payload);
    case 'updateFuel': return api.updateFuel(op.targetId, op.payload);
    case 'updateCarExpense': return api.updateCarExpense(op.targetId, op.payload);
    case 'addRecurring': return api.addRecurring(op.payload, once);
    case 'updateRecurring': return api.updateRecurring(op.targetId, op.payload);
    case 'deleteRecurring': return api.deleteRecurring(op.targetId);
    case 'deleteRate': return api.deleteRate(op.targetId, op.payload?.month);
    case 'settings': return api.settings(op.payload);
    case 'setBudget': return api.setBudget(op.payload.category, op.payload.amount, op.payload.currency);
    case 'addGoal': return api.addGoal(op.payload, once);
    case 'updateGoal': return api.updateGoal(op.targetId, op.payload);
    case 'deleteGoal': return api.deleteGoal(op.targetId);
    default: throw new Error(`Unknown queued operation: ${op.type}`);
  }
}

let flushing = false;

export async function flush({ silent = false } = {}) {
  if (sandbox) return;
  if (flushing || !queue.length || state.status !== 'ready') return;
  flushing = true;
  state.syncing = true;
  if (!silent) notify();

  let needsRefetch = false;

  try {
    while (queue.length) {
      const op = queue[0];
      let result;

      try {
        result = await runOp(op);
      } catch (error) {
        if (error instanceof OfflineError) {
          state.online = false;
          return; // Keep the queue; the next connectivity event retries it.
        }
        if (error instanceof ApiError && error.isAuthError) {
          /* The session ended mid-flush. What has not been sent is kept. */
          signOut({ keepQueue: true });
          return;
        }
        // The server rejected the change outright — replaying it will never
        // succeed, so drop it and resynchronise from the authoritative copy.
        queue.shift();
        persistQueue();
        state.error = errorKey(error);
        needsRefetch = true;
        continue;
      }

      state.online = true;
      queue.shift();
      persistQueue();

      if (op.tempId && result && result.id) {
        remapId(op.collection, op.tempId, result.id);
      }
      if (NEEDS_REFETCH.has(op.type) || op.refetch) needsRefetch = true;
    }

    if (needsRefetch) await refresh({ silent: true });
  } finally {
    flushing = false;
    state.syncing = false;
    notify();
  }
}

/**
 * Queues a mutation and applies it locally straight away.
 * `collection`/`tempId` let the queue rewrite the optimistic id once the server
 * assigns the real one.
 */
/*
 * A guest who has changed something has something of their own to keep --
 * which is what decides the default when they create an account. Called by
 * every edit: the queued ones (mutate) and those that go straight to the
 * server -- trades, backtests, prop firms, imports, fixed costs posted.
 */
function markGuestTouched() {
  if (state.user?.isGuest && ui.guestTouched !== state.user.id) {
    ui.guestTouched = state.user.id;
    persistUi();
  }
}

function mutate(op, mutator) {
  markGuestTouched();
  local(mutator);
  enqueue(op);
  notify();
  flush({ silent: true });
}

// ---------------------------------------------------------------- session ---

function adoptSession({ token, user }, { keepQueue = false } = {}) {
  const sameAccount = Boolean(state.user && user && state.user.id === user.id);

  setToken(token);
  state.user = user;
  /*
   * Anything queued before this session is kept only if it is this account's.
   *
   * It used to be dropped outright unless the caller said otherwise, which was
   * right about one account's edits never reaching another's ledger and wrong
   * about the commonest case: the same person signing back in after their
   * session ended under them, with work entered offline still unsent. The
   * stamp tells the two apart. Claiming a guest still passes `keepQueue`,
   * because that account is being renamed rather than swapped.
   */
  if (!keepQueue) adoptStoredQueue();

  /*
   * And the ledger on screen belongs to whoever was signed in a moment ago.
   * Signing in as somebody else must not leave their figures showing while the
   * new snapshot is still in flight — on a phone that window lasts seconds,
   * and what it shows is another account's money.
   */
  if (!sameAccount) {
    forgetSnapshot();
    delete ui.pricesRefreshed;
    persistUi();
  }
  netWorthRecorded = false;
}

/**
 * Writes down what the account is worth this month.
 *
 * The trend chart is drawn from recorded months, and nothing recorded them —
 * so it showed a single bar for ever. Holdings, debts and balances are current
 * figures with no past of their own, so a month cannot be reconstructed later:
 * the only honest history is the one kept as it happens.
 *
 * Once per session, for the real current month only — never for a month being
 * browsed, which would stamp today's net worth onto somebody's March.
 */
let netWorthRecorded = false;

async function recordNetWorth() {
  if (netWorthRecorded || !state.data || !state.user) return;

  const now = currentMonth();
  const net = computeMetrics(state.data, {
    month: now,
    strategy: state.settings.debtStrategy,
    unitSystem: state.settings.unitSystem,
    baseCurrency: state.settings.baseCurrency
  }).totals.netWorth;

  const stored = (state.data.history || []).find(row => row.month === now);
  if (stored && Math.abs(stored.net - net) < 0.005) {
    netWorthRecorded = true;
    return;
  }

  try {
    await api.recordNetWorth(now);
    netWorthRecorded = true;
    const history = (state.data.history || []).filter(row => row.month !== now);
    state.data.history = [...history, { month: now, net }].sort(
      (a, b) => a.month.localeCompare(b.month)
    );
    persistSnapshot();
    recompute();
  } catch {
    // A trend point is not worth interrupting anything for; the next load tries again.
  }
}

/**
 * Marks holdings to market once a day, without being asked.
 *
 * Pricing used to move only when somebody pressed a button, so a portfolio
 * quietly showed whatever it was worth the last time anyone thought to look.
 * Once per calendar day is enough: quotes are recorded per month, and a public
 * endpoint does not deserve a request per page load.
 */
async function autoRefreshPrices() {
  if (!state.data?.holdings?.length || !state.online) return;
  /*
   * Not on the demo: its year of prices is written to fit its own figures,
   * and a live quote for this month alone drew a cliff of +100,000 zł between
   * September and now on the chart a first visitor sees.
   */
  if (state.user?.isGuest) return;
  /* Automatic prices are Liquidity+; on Free the typed-in price stands. */
  if (state.user?.plan?.limits?.quotes === false) return;

  const today = localDay();
  if (ui.pricesRefreshed === today) return;

  try {
    const result = await api.refreshPrices();
    ui.pricesRefreshed = today;
    persistUi();
    if (result.updated) await refresh({ silent: true });
  } catch {
    // Marking to market is a convenience; a failed quote is not worth a toast.
  }
}

async function loadSnapshot({ silent = false } = {}) {
  const snapshot = await api.portfolio();
  checkPushOnce();
  state.data = snapshot;
  state.settings = { ...DEFAULT_SETTINGS, ...snapshot.settings };
  // Preferences belong to the account, so what the server holds wins over
  // whatever this device happened to be showing before sign-in -- and the
  // device remembers it, so the sign-in screen after a sign-out speaks it too.
  state.lang = state.settings.language || firstLanguage();
  persistUi();
  // "Opening screen" is a saved preference, so it decides the tab once per
  // launch. Tab changes after that are just navigation.
  if (!openingScreenApplied) {
    state.tab = state.settings.startTab || state.tab;
    openingScreenApplied = true;
  } else if (openedFromCache && state.tab === openedFromCache && state.settings.startTab && state.settings.startTab !== openedFromCache) {
    /*
     * The cached copy opened on the screen it remembered; the account's own
     * setting, changed on another device, has just arrived. Nothing has been
     * clicked since, so the setting still decides.
     */
    state.tab = state.settings.startTab;
  }
  openedFromCache = null;
  state.status = 'ready';
  state.online = true;
  /* A currency already in the ledger with no rate gets one too, not only a new entry's. */
  scheduleAutoRates();
  recompute();
  persistUi();
  persistSnapshot();
  if (!silent) notify();

  // Fire and forget: none of these is something the load waits on.
  recordNetWorth().then(() => notify());
  autoRefreshPrices();
  /*
   * The import review asks whether category suggestions are switched on. These
   * used to load only when the preferences panel rendered, so importing a
   * statement without having opened preferences first showed no suggest button
   * at all — and every expense went to Other, exactly as if the feature had
   * never been built.
   */
  loadAiSettings();

  return snapshot;
}

export async function refresh({ silent = false } = {}) {
  try {
    await loadSnapshot({ silent });
  } catch (error) {
    if (error instanceof OfflineError) {
      state.online = false;
      notify();
      return;
    }
    if (error instanceof ApiError && error.isAuthError) {
      signOut({ keepQueue: true });
      return;
    }
    state.error = errorKey(error);
    notify();
  }
}

async function loadDiscordStatus() {
  try {
    const status = await api.discordStatus();
    state.discordEnabled = Boolean(status?.enabled);
    notify();
  } catch {
    // Older server, or offline: the button simply stays hidden.
    state.discordEnabled = false;
  }
}

function handleOAuthResult({ ticket, error, verified, verifyError, reset }) {
  if (reset) {
    state.resetToken = reset;
    state.authPhase = 'reset';
    notify();
    return;
  }
  if (verified) {
    state.notice = 'verify:ok';
    // The account object in hand still says unverified; refresh it.
    if (getToken()) api.me().then(s => { state.user = s.user; notify(); }).catch(() => {});
    notify();
    return;
  }
  if (verifyError) {
    state.error = 'verify:failed';
    notify();
    return;
  }
  if (error) {
    state.authPhase = 'error';
    state.authErrorCode = error;
    notify();
    return;
  }
  if (ticket) completeDiscordSignIn(ticket);
}

/** Boots the app: resume a session, or fall back to the cached snapshot offline. */
/**
 * The live preview on the landing page: the real screens, drawn from a sample
 * ledger shipped with the app, with no account behind them.
 *
 * Sealed before anything is shown: no request leaves (`sealForPreview`), and
 * nothing is written to this origin's storage (`sandbox`). What a visitor
 * sees is the app as it is -- the same views, figures computed by the same
 * engine -- and none of it can touch a real ledger, including the one of a
 * person signed in to the app in the same browser.
 */
export function startPreview({ snapshot, trades = [], lang, tab = 'overview', month, user }) {
  sandbox = true;
  sealForPreview();
  queue = [];
  state.native = false;
  state.user = user;
  state.data = snapshot;
  state.trades = trades;
  state.settings = { ...DEFAULT_SETTINGS, ...snapshot.settings, language: lang, hideBalances: false };
  state.lang = lang;
  state.tab = TABS.includes(tab) ? tab : 'overview';
  state.tabFrom = 0;
  state.month = month || currentMonth();
  state.status = 'ready';
  state.authPhase = 'idle';
  state.online = true;
  state.error = null;
  state.notice = null;
  openingScreenApplied = true;
  recompute();
  notify();
}

export async function start() {
  loadDiscordStatus();
  onDeepLink(handleOAuthResult);

  /*
   * The app store shells do not sell: a subscription bought inside them would
   * be an in-app purchase by the stores' rules. They say where to buy instead.
   */
  state.native = isNativeShell();
  const billingReturn = takeBillingParams();

  // A Discord callback outranks any existing session — it is an explicit,
  // just-now instruction about which account to be.
  const callback = takeCallbackParams();
  if (callback) {
    handleOAuthResult(callback);
    // A ticket ends the boot because the exchange takes over from here. A reset
    // link does not: the ledger goes on loading behind the panel, so cancelling
    // lands the person back in their account rather than on a sign-in screen.
    if (callback.ticket) return;
  }

  /*
   * ?demo -- the landing page's "Try the demo". Starts one at once instead of
   * stopping at the sign-in screen with the same button on it a second time.
   * Somebody already signed in is simply let into their own account.
   */
  const params = new URLSearchParams(window.location.search);
  const wantsDemo = params.has('demo');
  if (wantsDemo) {
    params.delete('demo');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
  }

  if (!getToken()) {
    state.status = 'anon';
    notify();
    if (wantsDemo) signInAsGuest();
    return;
  }

  /*
   * An unstamped cache was written before ledgers were tied to an account, so
   * there is no telling whose it is: drop it rather than show it.
   */
  const cached = readJson(CACHE_KEY, null);
  if (cached && cached.data && cached.user) {
    state.data = cached.data;
    state.settings = { ...DEFAULT_SETTINGS, ...cached.data.settings };
    state.lang = state.settings.language || firstLanguage();
    if (!openingScreenApplied) {
      state.tab = state.settings.startTab || state.tab;
      openingScreenApplied = true;
      openedFromCache = state.tab;
    }
    state.status = 'ready';
    recompute();
    notify();
  }

  try {
    const session = await api.me();
    state.user = session.user;
    /* A queue held from a session that ended under us is taken up again here,
       once there is an account to check it against. */
    adoptStoredQueue();
    /*
     * The moment the account is known, anything cached for a different one goes
     * — without waiting for the replacement to arrive.
     */
    if (cached && cached.user !== session.user.id) {
      forgetSnapshot();
      notify();
    }
    await loadSnapshot();
    await flush();
    if (billingReturn) await settleBillingReturn(billingReturn);
  } catch (error) {
    if (error instanceof ApiError && error.isAuthError) {
      signOut({ keepQueue: true });
      return;
    }
    if (error instanceof OfflineError) {
      state.online = false;
      // A cached snapshot means we can carry on read-write until reconnection.
      state.status = state.data ? 'ready' : 'anon';
      if (!state.data) state.error = 'offline';
      notify();
      return;
    }
    /*
     * Anything else: land somewhere a person can act from.
     *
     * This used to set the message and stop, leaving `status` at 'loading'
     * forever — so one 500 from /api/auth/me, one bad gateway, one malformed
     * response at exactly the wrong moment, and the app sat on its splash
     * screen permanently. No retry, no sign-in, no way out but clearing site
     * data. A transient server hiccup must not brick the client.
     *
     * With a cached ledger the session carries on read-write, exactly as it
     * does when the network is down; without one, the sign-in screen is the
     * honest place to be, and it carries the error.
     */
    state.status = state.data ? 'ready' : 'anon';
    state.error = errorKey(error);
    notify();
  }
}

async function beginSession(promise, credentials = null) {
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  state.unverifiedEmail = null;
  notify();
  try {
    const session = await promise;
    adoptSession(session);
    await loadSnapshot();
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.code === 'email_unverified') {
      // Remember who was refused, so the panel can offer them a new link.
      state.unverifiedEmail = credentials?.email || null;
      state.unverifiedPassword = credentials?.password || null;
    }
    state.error = errorKey(error);
    return false;
  } finally {
    state.busy = false;
    notify();
  }
}

/*
 * The language on screen goes along with every way of making an account, so a
 * new one starts where its owner already was -- see /api/auth/register.
 */
/* The currency and units a new account starts in, from the country this browser's clock is set to. */
function startingPoint() {
  let zone = null;
  try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* no zone: the language decides */ }
  const country = countryOfTimeZone(zone);
  return { baseCurrency: homeCurrency(country, state.lang), unitSystem: homeUnits(country) };
}

export const signInAsGuest = () => beginSession(api.guest(state.lang, startingPoint()));
export const signIn = credentials => beginSession(api.login(credentials), credentials);
export const signUp = credentials =>
  beginSession(api.register({
    ...credentials, language: state.lang, ...startingPoint(), acceptTerms: state.termsTicked === true
  }), credentials);

export function setTermsTicked(on) {
  state.termsTicked = Boolean(on);
  notify();
}

/**
 * Accepts the current terms for an account that has not: one made through
 * Discord, one from before the documents existed, or any once they change.
 */
export async function acceptTerms({ next = false } = {}) {
  state.busy = true;
  state.error = null;
  notify();
  try {
    const result = await api.acceptTerms({ next });
    state.user = result.user;
    state.termsTicked = false;
    return true;
  } catch (error) {
    state.error = errorKey(error);
    return false;
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * Everything the account holds, as one JSON document -- the copy the privacy
 * policy promises. Read fresh from the server rather than from the cached
 * ledger, which can be behind.
 */
export async function exportAll() {
  try {
    /*
     * Assembled by the server from every table that holds the account's data.
     * It used to be put together here from the ledger, the journal and the prop
     * firms, which left out debt payments, import history, the weekly review,
     * fixed costs posted and more -- not "all of it", as the policy says.
     */
    return JSON.stringify(await api.exportMe(), null, 2);
  } catch (error) {
    state.error = errorKey(error);
    notify();
    return null;
  }
}

export function askDeleteAccount(open = true) {
  state.deleteOpen = Boolean(open);
  notify();
}

/** Deletes the account and everything in it, then signs out. */
export async function deleteAccount(password) {
  state.busy = true;
  state.error = null;
  notify();
  try {
    await api.deleteMe({ password, confirm: true });
    state.deleteOpen = false;
    signOut();
    state.notice = 'account:deleted';
    return true;
  } catch (error) {
    state.error = errorKey(error);
    return false;
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * Asks for a fresh confirmation link — with the session when there is one, and
 * with the refused credentials when sign-in was blocked before a session existed.
 */
export async function resendVerification() {
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const credentials = state.status === 'ready'
      ? null
      : { email: state.unverifiedEmail, password: state.unverifiedPassword };

    const result = await api.resendVerification(credentials);
    state.notice = result.emailSent ? 'verify:sent' : 'verify:notSent';
    return true;
  } catch (error) {
    state.error = errorKey(error);
    return false;
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * Whether a new account starts empty.
 *
 * Asked outright in the dialog. Until it is answered: empty for a guest who
 * only looked around -- the sample ledger is not theirs -- and keep for one
 * who entered anything, so nothing typed in the demo is lost by default.
 */
export function claimFresh() {
  if (state.claimFresh !== null && state.claimFresh !== undefined) return state.claimFresh;
  return !(state.user?.isGuest && ui.guestTouched === state.user.id);
}

export function setClaimFresh(fresh) {
  state.claimFresh = Boolean(fresh);
  notify();
}

export async function claimGuestAccount({ email, password, fresh = claimFresh() }) {
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const session = await api.claim({ email, password, fresh, acceptTerms: state.termsTicked === true });
    if (fresh) {
      /* Anything still waiting to be sent was an edit to the sample ledger, which is gone. */
      queue = [];
      persistQueue();
    }
    adoptSession(session, { keepQueue: !fresh });
    state.claimOpen = false;
    state.notice = fresh ? 'claimedFresh' : 'claimed';
    if (fresh) await refresh({ silent: true });
    return true;
  } catch (error) {
    state.error = errorKey(error);
    return false;
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * @param keepQueue  true when the session ended under us -- a password changed
 *                   elsewhere, an expired token -- rather than because somebody
 *                   asked to leave. Unsent changes survive to be replayed when
 *                   the same account signs back in.
 */
export function signOut({ keepQueue = false } = {}) {
  /* Before the token goes: the request that stops this browser's reminders needs it. */
  forgetPushDevice();
  setToken(null);
  if (keepQueue && queue.length) {
    /* Held under the owner it already has; `adoptStoredQueue` decides later
       whether whoever signs in may have it. */
    persistQueue();
    state.notice = 'queueHeld';
  } else {
    queue = [];
    queueOwner = null;
    persistQueue();
  }
  forgetSnapshot();

  state.status = 'anon';
  state.user = null;
  state.error = null;
  state.errorDetail = null;
  state.authPhase = 'idle';
  state.authErrorCode = null;
  state.pendingAccount = null;
  state.settingsOpen = false;
  state.adminOpen = false;
  state.admin = null;
  state.adminQuery = '';
  state.claimOpen = false;
  state.deleteOpen = false;
  state.termsTicked = false;
  /* A broker export read for this account is not the next one's to import. */
  state.tradeImport = null;
  /* Whoever comes back signs in: the form must not still be on the sign-up it was left on. */
  state.authMode = 'signIn';
  notify();
}

// ------------------------------------------------------------------- view ---

export function setTab(tab) {
  /*
   * Which way the new page comes in from.
   *
   * The tabs are a strip in a fixed order, so a page that always slid in from
   * the same side would contradict the nav the reader just used. Comparing
   * positions costs one lookup and makes the motion agree with the strip:
   * forward moves in from the right, back from the left.
   *
   * Not persisted -- it describes one transition, not a preference, and a
   * reload has no previous tab to have come from.
   */
  const from = TABS.indexOf(state.tab);
  const to = TABS.indexOf(tab);
  state.tabFrom = (from === -1 || to === -1 || from === to) ? 0 : (to > from ? 1 : -1);

  state.tab = tab;
  /* Leaving the overview closes the simulator opened from it. */
  state.whatIfOpen = false;
  state.whatIfFrom = 0;
  /*
   * The error belonged to the form that was on screen. Left standing it follows
   * the reader to a different tab and gets read as the reason THAT page's
   * button did nothing -- during testing a stale "balance must be between 0
   * and 10000000000" sat under the trade journal and was blamed for a failure
   * it had nothing to do with.
   */
  state.error = null;
  state.errorDetail = null;
  persistUi();
  notify();
}

export function stepMonth(delta) {
  state.month = shiftMonth(state.month, delta);
  persistUi();
  recompute();
  notify();
}

/**
 * Goes to a month directly, rather than a step at a time.
 *
 * The trend chart has always known which month each bar is; there was simply
 * nothing to tell. Twelve bars and a pair of arrows meant walking six months
 * back one press at a time to look at a column already on screen.
 *
 * Does the same bookkeeping `stepMonth` does, because arriving at a month has
 * to leave the app in the same state however you arrived.
 */
export function goToMonth(month) {
  if (!isMonth(month) || month === state.month) return;
  state.month = month;
  persistUi();
  recompute();
  notify();
}

export function setLanguage(lang) {
  state.lang = lang;
  state.settings = { ...state.settings, language: lang };
  rememberSettings({ language: lang });
  persistUi();
  // Before sign-in there is no account to store a preference against; the
  // choice still persists locally via `persistUi` and is applied on first load.
  if (state.status === 'ready') {
    enqueue({ type: 'settings', payload: { language: lang } });
    flush({ silent: true });
  }
  notify();
}

/** The currency new entries are recorded in; the base unless told otherwise. */
export function entryCurrency() {
  return state.entryCurrency || state.settings.baseCurrency;
}

export function setEntryCurrency(code) {
  state.entryCurrency = code === state.settings.baseCurrency ? null : code;
  persistUi();
  notify();
}

export function setCashBalance(currency, amount) {
  flashSaved();
  mutate(
    { type: 'setCashBalance', payload: { currency, amount } },
    data => {
      const balances = (data.cash || []).filter(b => b.currency !== currency);
      if (amount) balances.push({ currency, amount });
      data.cash = balances.sort((a, b) => a.currency.localeCompare(b.currency));
    }
  );
}

/**
 * The rate row in force for the month on screen, or null.
 *
 * The row rather than the number, so the panel can say where the figure came
 * from without working the rules out a second time and disagreeing.
 */
export function rateRow(currency) {
  return rateRowForMonth(state.data?.rateHistory || [], currency, state.month);
}

/** Every rate on record for one currency, newest month first. */
export function rateHistory(currency) {
  const code = String(currency || '').toUpperCase();
  return (state.data?.rateHistory || [])
    .filter(row => row.currency === code)
    .sort((a, b) => String(b.month || '').localeCompare(String(a.month || '')));
}

/**
 * Records a rate against the month currently on screen.
 *
 * Dating it is the whole point: entering September's rate must not re-value
 * August. Pass `{ month: null }` to set the standing rate instead, which is
 * what a rate with no particular month means.
 */
export function setRate(currency, rate, { month = state.month } = {}) {
  flashSaved();
  mutate(
    { type: 'setRate', payload: { currency, rate, month } },
    data => {
      const history = (data.rateHistory || []).filter(
        row => !(row.currency === currency && (row.month || null) === (month || null))
      );
      history.push({ currency, month: month || null, rate, source: 'manual' });
      data.rateHistory = history;
      data.rates = resolveRates(history, state.month);
    }
  );
}

/** Removes one month's rate, or the whole history when no month is given. */
export function removeRate(currency, month = undefined) {
  flashSaved();
  mutate(
    { type: 'deleteRate', targetId: currency, payload: { month } },
    data => {
      const history = (data.rateHistory || []).filter(row => row.currency !== currency
        || (month !== undefined && (row.month || null) !== (month || null)));
      data.rateHistory = history;
      data.rates = resolveRates(history, state.month);
    }
  );
}

/** Marks the ledger to today's rates. Needs the network, so it is not queued. */
export async function refreshRates() {
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const result = await api.refreshRates();
    await refresh({ silent: true });
    state.notice = result.missed?.length
      ? `rates:partial:${result.missed.join(', ')}`
      : 'rates:ok';
    return result;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

/** How long a deleted row can be put back. */
const UNDO_WINDOW_MS = 8000;
let undoTimer = null;

/**
 * Remembers a deleted row so it can be restored.
 *
 * Restoring re-creates rather than resurrects: the delete has usually already
 * reached the server by then, so the row comes back with a new id. Everything
 * a person can see about it is identical, which is what matters.
 */
function rememberDeletion(kind, row, restore) {
  clearTimeout(undoTimer);
  state.undo = { kind, row, restore };
  undoTimer = setTimeout(() => {
    if (state.undo && state.undo.row === row) {
      state.undo = null;
      notify();
    }
  }, UNDO_WINDOW_MS);
}

export function undoDelete() {
  const pending = state.undo;
  if (!pending) return;

  clearTimeout(undoTimer);
  state.undo = null;
  pending.restore(pending.row);
  notify();
}

export function dismissUndo() {
  clearTimeout(undoTimer);
  state.undo = null;
  notify();
}

// ------------------------------------------------------------- recurring ---

/**
 * Fixed costs whose entry for the month on screen has not been created yet.
 *
 * The decision itself lives in `shared/model.js` and the server reaches it the
 * same way. Two copies of "is this owed?" would eventually disagree, and the
 * disagreement people would notice is the button offering to add something the
 * server then declines to add.
 */
export function pendingRecurring() {
  return (state.data?.recurring || []).filter(
    template => recurringDue(template, state.month, state.data)
  );
}

/** Every template that charges in the month on screen, added already or not. */
export function recurringInMonth() {
  return (state.data?.recurring || []).filter(
    template => template.active && recurringFallsIn(template, state.month)
  );
}

export function askRecurringAgain() {
  state.recurringAgain = true;
  notify();
}

export function closeRecurringAgain() {
  state.recurringAgain = false;
  notify();
}

/**
 * What the repeating entries commit you to in an average month.
 *
 * A yearly bill counts as a twelfth here. The ledger never sees that number --
 * the entry it posts is the whole amount in the month it is actually charged --
 * but comparing a yearly subscription against a monthly one at face value would
 * understate it twelvefold, and the question this figure answers is how much of
 * a month is already spoken for before any decision gets made.
 *
 * Costs in a currency with no rate are counted separately rather than converted
 * at par, the same rule every other total in the app follows.
 */
export function fixedCostSummary(month = state.month) {
  const templates = (state.data?.recurring || []).filter(t => t.active);
  const rates = resolveRates(state.data?.rateHistory || [], month);
  const base = state.data?.settings?.baseCurrency || DEFAULT_SETTINGS.baseCurrency;

  const summary = {
    expense: 0, income: 0, count: templates.length,
    /** Templates whose currency has no rate, so they are in no total. */
    unconverted: [],
    /** Everything charged in the month on screen, soonest day first. */
    dueThisMonth: [],
    /** The first month from now that any of them is charged in. */
    nextMonth: null
  };

  for (const template of templates) {
    const rate = template.currency === base ? 1 : rates[template.currency];
    if (!rate) {
      summary.unconverted.push(template);
    } else {
      summary[template.kind === 'income' ? 'income' : 'expense'] +=
        monthlyEquivalent(template) * rate;
    }

    if (recurringDue(template, month, state.data) || recurringPosted(template, month, state.data)) {
      summary.dueThisMonth.push(template);
    }

    const next = nextChargeMonth(template, currentMonth());
    if (next && (!summary.nextMonth || next < summary.nextMonth)) summary.nextMonth = next;
  }

  summary.dueThisMonth.sort((a, b) => (a.dayOfMonth || 99) - (b.dayOfMonth || 99));
  return summary;
}

/**
 * What is about to be charged, soonest first.
 *
 * Everything else on the overview looks backwards -- what came in, what went
 * out, what a month closed at. This is the one thing the app knows that nobody
 * can read off a bank statement: a yearly renewal three weeks out, on a day it
 * was told about months ago and has not mentioned since.
 *
 * A charge already entered in the ledger for its month is kept in the list and
 * marked, not dropped. Posting is bookkeeping; the money still leaves on the
 * day, and a list that hid it would be answering a different question.
 */
export function upcomingCharges({ days = 45, now = new Date() } = {}) {
  const today = localDay(now);
  const base = state.data?.settings?.baseCurrency || DEFAULT_SETTINGS.baseCurrency;
  const rates = resolveRates(state.data?.rateHistory || [], currentMonth(now));

  const rows = [];
  for (const template of state.data?.recurring || []) {
    if (!template.active) continue;

    const next = nextChargeDate(template, now);
    if (!next) continue;

    /* A charge with no day stated has no place on a countdown -- it is listed
       under its month instead, after everything that does have one. */
    const inDays = next.date ? daysBetween(today, next.date) : null;
    if (inDays !== null && inDays > days) continue;
    if (inDays === null && next.month !== currentMonth(now)) continue;

    const rate = template.currency === base ? 1 : rates[template.currency];
    rows.push({
      id: template.id,
      kind: template.kind,
      label: template.label,
      category: template.category || null,
      amount: template.amount,
      currency: template.currency,
      /** Null when the currency has no rate, so it is left out of the total. */
      inBase: rate ? template.amount * rate : null,
      everyMonths: template.everyMonths,
      month: next.month,
      date: next.date,
      inDays,
      /** Already written into the ledger for that month. */
      posted: recurringPosted(template, next.month, state.data)
    });
  }

  rows.sort((a, b) => {
    if (a.inDays === null) return b.inDays === null ? 0 : 1;
    if (b.inDays === null) return -1;
    return a.inDays - b.inDays;
  });

  const outgoing = rows.filter(r => r.kind === 'expense' && r.inBase !== null);
  return {
    rows,
    days,
    /** What leaves the account inside the window, in the base currency. */
    total: outgoing.reduce((sum, r) => sum + r.inBase, 0),
    /** Costs in a currency with no rate, so absent from that total. */
    unconverted: rows.filter(r => r.inBase === null).length
  };
}

// ------------------------------------------------------ payment reminders ---

/*
 * Notifications on this phone or computer before a fixed cost, a
 * subscription or an instalment is due (server/src/lib/reminders.js). A
 * device turns them on for itself; what they cover is the account's.
 */

/** What this browser can do: 'ok', 'ios-home' (iPhone/iPad, not on the Home Screen), 'native' (the app shells) or 'none'. */
export function pushSupport() {
  if (typeof window === 'undefined') return 'none';
  if (window.Capacitor?.isNativePlatform?.()) return 'native';
  const capable = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (capable) return 'ok';
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
  return ios && !standalone ? 'ios-home' : 'none';
}

const urlKey = text => {
  const padded = (text + '='.repeat((4 - (text.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(padded), c => c.charCodeAt(0));
};
const keyText = buffer => btoa(String.fromCharCode(...new Uint8Array(buffer)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/* The service worker, but no waiting forever for one that never registers (development, a blocked script). */
async function pushRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  return Promise.race([navigator.serviceWorker.ready, new Promise(resolve => setTimeout(() => resolve(null), 4000))]);
}

/*
 * This browser's own note of its reminders: which account turned them on here,
 * and at which push address. Kept on the device because the server cannot
 * know it -- and two things need it before Preferences is ever opened:
 *
 *  - signing out stops the reminders. It used to look only at what
 *    Preferences had loaded this session, so signing out without opening it
 *    left the browser receiving that account's bills and amounts;
 *  - a push service that rotates or expires the address (Firefox and Apple
 *    both do) makes the server drop the device, and reminders stopped with
 *    nothing on screen to say so. The app now re-registers on its next start.
 */
const PUSH_KEY = 'pt.push';
const pushNote = () => readJson(PUSH_KEY, null);
const notePush = endpoint => writeJson(PUSH_KEY, endpoint && state.user ? { user: state.user.id, endpoint } : null);
let pushCheckedFor = null;

/* Once per account per start: if this browser had reminders on for it, make sure the server still has them. */
function checkPushOnce() {
  const id = state.user?.id;
  if (!id || pushCheckedFor === id || state.user.isGuest) return;
  pushCheckedFor = id;
  /*
   * Also when there is no note but the browser lets this site notify: a
   * browser turned on before the note existed gets one written by loadPush.
   */
  const granted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
  if (pushNote()?.user === id || (granted && pushSupport() === 'ok')) loadPush();
}

/* Registers this browser with the server: the current subscription if it matches the server key, a new one if not. */
async function registerBrowser(key) {
  const registration = await pushRegistration();
  if (!registration) return null;
  let subscription = await registration.pushManager.getSubscription();
  /* Made against another server key -- a reset server, or another build: start afresh. */
  if (subscription) {
    const held = subscription.options?.applicationServerKey;
    if (!held || keyText(held) !== key) { await subscription.unsubscribe(); subscription = null; }
  }
  subscription ||= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlKey(key) });
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  await api.pushSubscribe(subscription.toJSON(), zone);
  notePush(subscription.endpoint);
  return subscription;
}

async function browserSubscription() {
  const registration = await pushRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
}

/** Where this browser stands, fetched when Preferences opens. */
export async function loadPush() {
  if (state.push.loading) return;
  const support = pushSupport();
  state.push = { ...state.push, support, loading: true, permission: support === 'ok' ? Notification.permission : null };
  if (!state.user || state.user.isGuest || !state.online || support !== 'ok') {
    state.push = { ...state.push, loading: false, loaded: support !== 'ok' || !state.online };
    notify();
    return;
  }
  try {
    let subscription = support === 'ok' ? await browserSubscription() : null;
    let result = await api.pushState(subscription?.endpoint || null);
    /*
     * Turned on here, for this account, and the server has lost it (or the
     * browser has a new address): put it back without asking -- permission
     * was given, and the reminders were never turned off.
     */
    if (!result.thisDevice && pushNote()?.user === state.user.id && Notification.permission === 'granted') {
      subscription = await registerBrowser(result.publicKey).catch(() => subscription);
      if (subscription) result = await api.pushState(subscription.endpoint);
    }
    if (result.thisDevice && subscription) notePush(subscription.endpoint);
    /* Blocked in the browser's own settings since: nothing to put back until it is allowed and turned on again. */
    if (!result.thisDevice && pushNote()?.user === state.user.id && Notification.permission === 'denied') notePush(null);
    state.push = {
      ...state.push, loaded: true, publicKey: result.publicKey, prefs: result.prefs, devices: result.devices,
      on: Boolean(subscription && result.thisDevice), endpoint: subscription?.endpoint || null
    };
  } catch {
    state.push = { ...state.push, loaded: true };
  }
  state.push = { ...state.push, loading: false };
  notify();
}

export async function enablePush() {
  if (state.push.busy) return;
  /* Whatever went wrong last time is not what this click is about. */
  state.error = null;
  state.push = { ...state.push, busy: true };
  notify();
  try {
    const permission = await Notification.requestPermission();
    state.push = { ...state.push, permission };
    if (permission !== 'granted') { state.error = 'push:blocked'; return; }
    const key = state.push.publicKey || (await api.pushState()).publicKey;
    const subscription = await registerBrowser(key);
    if (!subscription) { state.error = 'push:unsupported'; return; }
    state.push = { ...state.push, on: true, endpoint: subscription.endpoint };
    state.notice = 'push:on';
  } catch (error) {
    state.error = error instanceof ApiError ? errorKey(error) : 'push:failed';
  } finally {
    state.push = { ...state.push, busy: false };
    notify();
    loadPush();
  }
}

export async function disablePush() {
  if (state.push.busy) return;
  /* Whatever went wrong last time is not what this click is about. */
  state.error = null;
  state.push = { ...state.push, busy: true };
  notify();
  try {
    const subscription = await browserSubscription();
    if (subscription) {
      await api.pushUnsubscribe(subscription.endpoint).catch(() => null);
      await subscription.unsubscribe().catch(() => null);
    }
    notePush(null);
    state.push = { ...state.push, on: false, endpoint: null };
    state.notice = 'push:off';
  } finally {
    state.push = { ...state.push, busy: false };
    notify();
    loadPush();
  }
}

/** What the reminders cover and how early: for the account, so every device follows. */
export async function setPushPrefs(patch) {
  flashSaved();
  const before = state.push.prefs;
  state.push = { ...state.push, prefs: { ...before, ...patch } };
  notify();
  try {
    const result = await api.pushPrefs(patch);
    state.push = { ...state.push, prefs: result.prefs };
  } catch (error) {
    state.push = { ...state.push, prefs: before };
    state.error = errorKey(error);
  }
  notify();
}

let testedTimer = null;

export async function testPush() {
  if (!state.push.endpoint || state.push.testing) return;
  state.error = null;
  state.push = { ...state.push, testing: true, tested: false };
  notify();
  try {
    const result = await api.pushTest(state.push.endpoint);
    if (result.outcome === 'sent') {
      state.notice = 'push:test';
      /* The button itself says it went, for a few seconds: a toast at the foot of the screen is easy to miss. */
      state.push = { ...state.push, tested: true };
      clearTimeout(testedTimer);
      testedTimer = setTimeout(() => { state.push = { ...state.push, tested: false }; notify(); }, 6000);
    } else {
      state.error = 'push:failed';
      if (result.outcome === 'gone') state.push = { ...state.push, on: false };
    }
  } catch (error) {
    state.error = errorKey(error);
  }
  state.push = { ...state.push, testing: false };
  notify();
}

/* Signing out of a shared browser: that account's reminders stop coming to it. */
function forgetPushDevice() {
  const note = pushNote();
  const endpoint = state.push?.on ? state.push.endpoint
    : note && note.user === state.user?.id ? note.endpoint : null;
  notePush(null);
  pushCheckedFor = null;
  state.push = { support: state.push?.support || null };
  if (!endpoint) return;
  api.pushUnsubscribe(endpoint).catch(() => null);
  browserSubscription().then(subscription => subscription?.unsubscribe()).catch(() => null);
}

// ------------------------------------------------------- budgets & goals ---

/**
 * A category's monthly limit, in the base currency; zero or nothing takes it
 * away. Through the queue like any other change, so it works offline.
 */
export function setBudget(category, amount) {
  const currency = state.settings.baseCurrency;
  const value = Number(amount) > 0 ? Number(amount) : 0;
  mutate(
    { type: 'setBudget', payload: { category, amount: value, currency } },
    data => {
      data.budgets = (data.budgets || []).filter(row => row.category !== category);
      if (value > 0) data.budgets.push({ category, amount: value, currency });
    }
  );
}

export function setBudgetUi(patch) {
  state.budgetUi = { ...state.budgetUi, ...patch };
  notify();
}

export function setGoalUi(patch) {
  state.goalUi = { ...state.goalUi, ...patch };
  notify();
}

/** A new goal: name, target and, if given, what is saved, what goes in monthly and a deadline. */
export function addGoal({ name, target, saved = 0, monthly = 0, deadline = null, currency = state.settings.baseCurrency }) {
  const id = tempId();
  const payload = { name, target, saved, monthly, currency, ...(deadline ? { deadline } : {}) };
  mutate(
    { type: 'addGoal', collection: 'goals', tempId: id, payload },
    data => { data.goals = [...(data.goals || []), { id, ...payload, deadline: deadline || null }]; }
  );
}

export function updateGoal(id, patch) {
  mutate(
    { type: 'updateGoal', targetId: id, payload: patch },
    data => {
      const row = (data.goals || []).find(goal => goal.id === id);
      if (row) Object.assign(row, patch);
    }
  );
}

export function deleteGoal(id) {
  mutate(
    { type: 'deleteGoal', targetId: id },
    data => { data.goals = (data.goals || []).filter(goal => goal.id !== id); }
  );
}

export function addRecurring({
  kind, label, amount, currency = entryCurrency(), active = true, lastPostedMonth = null,
  everyMonths = 1, firstMonth = null, untilMonth = null, dayOfMonth = null,
  postedMonths = null, category = null
}) {
  const id = tempId();
  /* A cadence longer than a month needs a month to count from, and the local
     copy has to agree with what the server will store or the list flickers
     between the two on the next refetch. */
  const anchor = firstMonth || (everyMonths !== 1 ? currentMonth() : null);
  const posted = postedMonths || (lastPostedMonth ? [lastPostedMonth] : []);

  mutate(
    {
      type: 'addRecurring', collection: 'recurring', tempId: id,
      /*
       * Optional fields are left out rather than sent empty. A field carrying
       * null is a statement, and there is nothing to state on a template being
       * created for the first time.
       */
      payload: {
        kind, label, amount, currency, active, everyMonths,
        /* Expenses only: an income's label is its source and always has been. */
        ...(kind === 'expense' && category ? { category } : {}),
        ...(anchor ? { firstMonth: anchor } : {}),
        ...(untilMonth ? { untilMonth } : {}),
        ...(dayOfMonth ? { dayOfMonth } : {}),
        ...(posted.length ? { postedMonths: posted } : {})
      }
    },
    data => data.recurring.push({
      id, kind, label, amount, currency, active,
      category: kind === 'expense' ? category : null,
      everyMonths, firstMonth: anchor, untilMonth, dayOfMonth,
      postedMonths: posted,
      lastPostedMonth: posted.length ? posted[posted.length - 1] : null
    })
  );
}

export function updateRecurring(id, patch) {
  mutate(
    { type: 'updateRecurring', targetId: id, payload: patch },
    data => {
      const row = data.recurring.find(t => t.id === id);
      if (!row) return;
      Object.assign(row, patch);
      /* Same rule the server applies, applied here so the row on screen does
         not sit anchorless until the next refetch corrects it. */
      if (row.everyMonths !== 1 && !row.firstMonth) row.firstMonth = currentMonth();
    }
  );
}

export function removeRecurring(id) {
  const removed = state.data?.recurring.find(t => t.id === id);
  local(data => { data.recurring = data.recurring.filter(t => t.id !== id); });
  if (isTemp(id) && dropQueuedCreate(id)) return notify();
  mutate({ type: 'deleteRecurring', targetId: id }, () => {});
  /*
   * The whole row goes back, not just its wording. A template restored without
   * the month it had already posted looks untouched, and the next post charges
   * that month a second time.
   */
  if (removed) rememberDeletion('recurring', removed, row => addRecurring(row));
}

/**
 * Creates this month's entries from the outstanding templates.
 *
 * Not queued: the server decides what is outstanding, and replaying a stale
 * decision from an offline queue is exactly how a month gets charged twice.
 */
export async function postRecurring({ again = false } = {}) {
  markGuestTouched();
  if (state.busy) return null;
  state.busy = true;
  state.recurringAgain = false;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const result = await api.postRecurring(state.month, again);
    await refresh({ silent: true });
    state.notice = result.posted ? `recurring:posted:${result.posted}` : 'recurring:none';
    return result;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

export function setSheet(sheet) {
  state.sheet = sheet;
  /*
   * A date picked in a sheet does not outlive it.
   *
   * The same lesson the edit dialogs taught: the value lives in state rather
   * than in an input, so nothing clears it when the form goes away, and the
   * calendar's open slot is keyed by field name — leaving either behind means
   * the next sheet opens showing somebody else's answer, or opens with a
   * calendar spread across it that nobody asked for.
   */
  state.vehicleBought = undefined;
  state.monthPicker = null;
  state.monthPickerYear = null;
  state.datePickerMonth = null;
  notify();
}

export function dismissError() {
  state.error = null;
  state.errorDetail = null;
  state.notice = null;
  notify();
}

/**
 * What to record for an error, so the view can say it in the reader's language.
 *
 * Server validation messages are English sentences written for whoever reads
 * the logs — `"balance" must be between 0 and 10000000000.` — and they were
 * being shown verbatim to somebody using the app in Polish, internal field name
 * and all. The code and the field survive; the sentence does not.
 */
function errorKey(error) {
  /*
   * The server's own sentence is kept beside the key rather than thrown away.
   * It is no use to somebody adding a holding -- they get the translated
   * version -- but the sign-in screen is where a misconfigured deployment
   * announces itself, and there the exact words are the whole diagnostic.
   */
  state.errorDetail = error instanceof ApiError ? error.message : null;

  if (error instanceof OfflineError) return 'offline';
  if (error instanceof ApiError) return error.key;
  return error?.message || 'error';
}

export function showError(message) {
  state.error = message;
  notify();
}

export function setAuthMode(mode) {
  state.authMode = mode;
  state.error = null;
  state.errorDetail = null;
  notify();
}

export function openClaim() {
  state.claimOpen = true;
  state.claimFresh = null;
  state.error = null;
  state.errorDetail = null;
  notify();
}

export function closeClaim() {
  state.claimOpen = false;
  notify();
}

/**
 * Opens Preferences, on a section when one is named: the missing-rate warning
 * opens Currencies, "see the plans" opens Plan. Called straight from click
 * handlers too, which pass an event -- anything but a name opens the default.
 */
export function openSettings(section) {
  state.settingsOpen = true;
  state.accountMenuOpen = false;
  state.settingsSection = typeof section === 'string' ? section : null;
  notify();
}

export function setSettingsSection(section) {
  state.settingsSection = section || null;
  state.passwordOpen = false;
  state.deleteOpen = false;
  notify();
}

export function addSettingsCurrency(code) {
  if (!state.settingsAddedCodes.includes(code)) state.settingsAddedCodes = [...state.settingsAddedCodes, code];
  notify();
}

export function forgetAddedCurrency(code) {
  state.settingsAddedCodes = state.settingsAddedCodes.filter(item => item !== code);
  notify();
}

export function toggleAccountMenu(open = !state.accountMenuOpen) {
  state.accountMenuOpen = Boolean(open);
  notify();
}

let savedTimer = null;
/* Preferences save as they change; the header says so for a moment after each one. */
function flashSaved() {
  if (!state.settingsOpen) return;
  state.settingsSaved = true;
  clearTimeout(savedTimer);
  savedTimer = setTimeout(() => { state.settingsSaved = false; notify(); }, 1600);
}

export function copyVersion() {
  try { navigator.clipboard?.writeText(APP_VERSION); } catch { /* the label still says which version it is */ }
  state.versionCopied = true;
  notify();
  setTimeout(() => { state.versionCopied = false; notify(); }, 1500);
}

/**
 * Closes the launch-gift announcement for good: here at once, and on the
 * account as soon as the server hears, so another device does not show it
 * again. A failed call costs nothing worse than one more showing elsewhere.
 * `showPlan` opens Preferences, where the Plan section says the rest.
 */
export function dismissGift({ showPlan = false } = {}) {
  if (state.user) state.user = { ...state.user, giftSeen: true };
  if (showPlan) { state.settingsOpen = true; state.settingsSection = 'plan'; }
  notify();
  api.giftSeen()
    .then(result => {
      if (result?.user) {
        state.user = result.user;
        notify();
      }
    })
    .catch(() => {});
}

export function closeSettings() {
  state.passwordOpen = false;
  state.settingsOpen = false;
  state.settingsSection = null;
  state.settingsAddedCodes = [];
  state.deleteOpen = false;
  notify();
}

// ------------------------------------------------------------------ admin ---

/**
 * The admin panel. Offered only to an account the server marks as an admin,
 * and the server checks every one of these requests again for itself.
 */
export function openAdmin() {
  if (!state.user?.isAdmin) return;
  state.settingsOpen = false;
  state.adminOpen = true;
  notify();
  loadAdmin();
}

export function closeAdmin() {
  state.adminOpen = false;
  notify();
}

export function setAdminQuery(query) {
  state.adminQuery = query;
  notify();
}

export async function loadAdmin() {
  state.admin = { ...(state.admin || {}), loading: true, failed: false };
  notify();
  try {
    const [stats, accounts] = await Promise.all([api.adminStats(), api.adminAccounts()]);
    state.admin = { stats, accounts, loading: false, failed: false };
  } catch (error) {
    state.admin = { ...(state.admin || {}), loading: false, failed: true };
    state.error = errorKey(error);
  }
  notify();
}

/** Liquidity+ granted by hand -- with an end date or without -- or taken back. */
export async function adminSetPlan(id, plan, until = null) {
  if (state.busy) return;
  state.busy = true;
  state.error = null;
  notify();
  try {
    const account = await api.adminSetPlan(id, until ? { plan, until } : { plan });
    if (state.admin?.accounts) {
      state.admin.accounts = state.admin.accounts.map(row => (row.id === id ? account : row));
    }
    state.notice = 'admin:saved:' + account.email;
    /* The counts move with it; and a change to one's own plan shows at once. */
    api.adminStats().then(stats => {
      if (state.admin) state.admin.stats = stats;
      notify();
    }).catch(() => {});
    if (id === state.user?.id) api.me().then(s => { state.user = s.user; notify(); }).catch(() => {});
  } catch (error) {
    state.error = errorKey(error);
  } finally {
    state.busy = false;
    notify();
  }
}

// --------------------------------------------------------- discord oauth ---

/**
 * Starts the handshake. The server mints state and the PKCE challenge; all this
 * side does is say where to come back to and hand the user over to Discord.
 */
export async function startDiscordSignIn({ link = false } = {}) {
  state.authPhase = 'connecting';
  state.authErrorCode = null;
  state.error = null;
  state.errorDetail = null;
  state.busy = true;
  notify();

  try {
    const target = returnUrl();
    const { authorizeUrl } = link
      ? await api.discordLinkStart(target)
      : await api.discordStart(target, state.lang, startingPoint());
    await openAuthorizeUrl(authorizeUrl);
    // On the web this navigates away; on native the deep-link listener resumes.
  } catch (error) {
    state.authPhase = 'error';
    state.authErrorCode = error instanceof OfflineError ? 'offline' : (error.code || 'start_failed');
    state.error = errorKey(error);
  } finally {
    state.busy = false;
    notify();
  }
}

/** Trades the single-use ticket the callback returned for a real session. */
async function completeDiscordSignIn(ticket) {
  state.authPhase = 'connecting';
  state.busy = true;
  notify();

  try {
    const session = await api.discordExchange(ticket);
    adoptSession(session);
    // Hold at the confirmation card so the user can see which account connected.
    state.pendingAccount = session.user;
    state.authPhase = 'done';
  } catch (error) {
    state.authPhase = 'error';
    state.authErrorCode = error instanceof OfflineError ? 'offline' : (error.code || 'exchange_failed');
    state.error = errorKey(error);
  } finally {
    state.busy = false;
    notify();
  }
}

/** Confirms the connected account and loads the ledger. */
export async function enterLedger() {
  state.busy = true;
  notify();
  try {
    await loadSnapshot();
    state.pendingAccount = null;
    state.authPhase = 'idle';
  } catch (error) {
    state.error = errorKey(error);
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * Asks for a reset link.
 *
 * The answer is the same whether or not the address is on file, so the copy
 * shown afterwards has to be too -- promising a link only for known addresses
 * would turn this screen into a way to find out who has an account.
 */
export async function requestReset(email) {
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    await api.forgotPassword(email);
    state.notice = 'reset:sent';
    state.authMode = 'signIn';
    return true;
  } catch (error) {
    state.error = errorKey(error);
    return false;
  } finally {
    state.busy = false;
    notify();
  }
}

/** Spends the link and signs in with the password just chosen. */
export async function submitReset(password) {
  if (!state.resetToken) return false;

  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const session = await api.resetPassword(state.resetToken, password);
    // Spent, whatever happens next.
    state.resetToken = null;
    state.authPhase = 'idle';
    adoptSession(session);
    state.notice = 'reset:done';
    await refresh();
    return true;
  } catch (error) {
    if (error instanceof ApiError && error.status === 410) {
      state.resetToken = null;
      state.authPhase = 'idle';
      state.error = 'reset:expired';
    } else {
      state.error = errorKey(error);
    }
    return false;
  } finally {
    state.busy = false;
    notify();
  }
}

/** Backs out of the reset screen without spending the link. */
export function cancelReset() {
  state.resetToken = null;
  state.authPhase = 'idle';
  state.error = null;
  state.errorDetail = null;
  notify();
}

export function resetAuth() {
  state.authPhase = 'idle';
  state.authErrorCode = null;
  state.error = null;
  state.errorDetail = null;
  state.pendingAccount = null;
  if (state.status !== 'ready') setToken(null);
  notify();
}

export async function unlinkDiscord() {
  state.busy = true;
  notify();
  try {
    const result = await api.discordUnlink();
    state.user = result.user;
  } catch (error) {
    state.error = errorKey(error);
  } finally {
    state.busy = false;
    notify();
  }
}

export function openMonthPicker(id, value) {
  state.monthPicker = id;
  state.monthPickerYear = Number(String(value || '').slice(0, 4)) || new Date().getFullYear();
  notify();
}

export function closeMonthPicker() {
  state.monthPicker = null;
  state.monthPickerYear = null;
  state.datePickerMonth = null;
  notify();
}

export function stepMonthPickerYear(by) {
  state.monthPickerYear = (state.monthPickerYear || new Date().getFullYear()) + by;
  notify();
}

/*
 * The day picker reuses the month picker's "only one open at a time" slot, and
 * adds the month it is showing. Two separate open-flags would eventually let
 * both be open at once, stacked, with no way to tell which a click belonged to.
 */
/** Stores a date chosen in the app's calendar; '' clears back to today. */
export function setPickedDate(key, stamp) {
  state[key] = stamp || '';
  notify();
}

export function openDatePicker(id, value) {
  const [year, month] = String(value || '').split('-');
  const now = new Date();
  state.monthPicker = id;
  state.monthPickerYear = Number(year) || now.getFullYear();
  state.datePickerMonth = Number(month) || now.getMonth() + 1;
  notify();
}

/** Steps a month, rolling the year over at both ends. */
export function stepDatePickerMonth(by) {
  const now = new Date();
  let month = (state.datePickerMonth || now.getMonth() + 1) + by;
  let year = state.monthPickerYear || now.getFullYear();

  while (month > 12) { month -= 12; year += 1; }
  while (month < 1) { month += 12; year -= 1; }

  state.datePickerMonth = month;
  state.monthPickerYear = year;
  notify();
}

export function togglePassword() {
  state.passwordOpen = !state.passwordOpen;
  state.error = null;
  state.errorDetail = null;
  notify();
}

export async function setPassword(payload) {
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    /* This browser keeps its reminders; the devices signed out with the old password lose theirs. */
    const result = await api.setPassword({ ...payload, pushEndpoint: state.push.on ? state.push.endpoint : undefined });
    state.user = result.user;
    state.notice = 'password:ok';
    state.passwordOpen = false;
    return true;
  } catch (error) {
    state.error = errorKey(error);
    return false;
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * Every preference lives on the account, so a change is queued to the server
 * exactly like a ledger edit — and comes back on the next device that signs in.
 */
export function setSettings(patch) {
  flashSaved();
  /*
   * A new base currency restates every rate (rebaseRateHistory). The server
   * does it on save; doing the same here keeps the screen right in between --
   * it used to read złoty rates as euro ones until the next reload, and show
   * dollar holdings at four times their value.
   */
  const from = normaliseCurrency(state.settings?.baseCurrency);
  const to = patch.baseCurrency ? normaliseCurrency(patch.baseCurrency) : from;
  if (state.data && from && to && from !== to) {
    state.data.rateHistory = rebaseRateHistory(state.data.rateHistory || [], from, to);
    state.data.rates = resolveRates(state.data.rateHistory, state.month);
  }
  state.settings = { ...state.settings, ...patch };
  if (patch.language) state.lang = patch.language;
  rememberSettings(patch);
  persistUi();
  recompute();
  enqueue({ type: 'settings', payload: patch });
  notify();
  flush({ silent: true });
}

// ---------------------------------------------------------------- actions ---

export function addIncome({ label, amount, currency = entryCurrency() }) {
  const id = tempId();
  const month = state.month;
  mutate(
    { type: 'addIncome', collection: 'income', tempId: id, payload: { month, label, amount, currency } },
    data => data.income.push({ id, month, label, amount, currency, key: null })
  );
}

export function removeIncome(id) {
  const removed = state.data?.income.find(r => r.id === id);
  local(data => { data.income = data.income.filter(r => r.id !== id); });
  if (removed) rememberDeletion('income', removed, row => addIncome(row));
  if (isTemp(id) && dropQueuedCreate(id)) return notify();
  mutate({ type: 'deleteIncome', targetId: id }, () => {});
}

export function addExpense({ label, amount, currency = entryCurrency(), note = null }) {
  const id = tempId();
  const month = state.month;
  /* The name it was spent on, when given: "Biedronka" under Groceries. */
  const text = typeof note === 'string' && note.trim() ? note.trim() : null;
  mutate(
    {
      type: 'addExpense', collection: 'expenses', tempId: id,
      payload: { month, label, amount, currency, ...(text ? { note: text } : {}) }
    },
    data => data.expenses.push({
      id, month, label, amount, currency, note: text, origin: null, originId: null
    })
  );
}

export function removeExpense(id, { offerUndo = true } = {}) {
  const removed = state.data?.expenses.find(r => r.id === id);
  /*
   * A Debt-service line posted by a payment takes the payment with it -- the
   * server does the same -- so the debt goes back up as the line disappears.
   * Putting it back logs the payment again rather than adding a bare line.
   */
  const paid = removed?.origin === 'debt_payment';
  const debt = paid ? paymentDebt(removed) : null;
  local(data => {
    data.expenses = data.expenses.filter(r => r.id !== id);
    const target = debt && data.debts.find(d => d.id === debt.id);
    if (target) {
      target.history = debtHistoryWithoutPayment(target, removed.month, removed.amount);
      target.balance = debtLatestBalance(target);
    }
  });
  if (removed && offerUndo && !paid) rememberDeletion('expense', removed, row => addExpense(row));
  if (removed && offerUndo && debt) {
    rememberDeletion('expense', removed, row => payDebt(debt.id, row.amount, { month: row.month, offerUndo: false }));
  }
  if (isTemp(id) && dropQueuedCreate(id)) return notify();
  mutate({ type: 'deleteExpense', targetId: id, refetch: paid }, () => {});
}

/**
 * The debt a Debt-service line was paid against. The line knows its payment,
 * not its debt, so a line made on this device carries the debt's id and one
 * from the server is matched by name -- only when the name is unambiguous;
 * otherwise the fresh copy fetched after the delete settles it.
 */
function paymentDebt(line) {
  const debts = state.data?.debts || [];
  if (line.debtId) return debts.find(d => d.id === line.debtId) || null;
  const named = debts.filter(d => d.name === line.note && d.currency === line.currency);
  return named.length === 1 ? named[0] : null;
}

/**
 * On the books from the month on screen, like every other entry -- the debt
 * tab shows what was owed month by month. A debt being put back after a
 * delete brings its months with it, not just the balance it ended on.
 */
export function addDebt({
  name, balance, original, rate, payment, currency = entryCurrency(),
  term = null, fees = 0, startMonth = null, history = null, kind = 'loan', payDay = null
}) {
  const id = tempId();
  const month = state.month;
  const restored = debtHistory({ history });
  const recorded = restored.length ? restored : [{ month, balance: Math.max(0, Number(balance) || 0) }];
  const row = {
    id, name, balance: debtLatestBalance({ history: recorded }), original: original || balance,
    rate, payment, currency, term: term || null, fees: fees || 0, startMonth: startMonth || null,
    history: recorded, kind: kind === 'card' ? 'card' : 'loan', payDay: payDay || null
  };
  mutate(
    {
      type: 'addDebt', collection: 'debts', tempId: id,
      payload: {
        name, balance: row.balance, original: row.original, rate, payment, currency,
        term: row.term, fees: row.fees, startMonth: row.startMonth, kind: row.kind, payDay: row.payDay,
        ...(restored.length ? { history: recorded } : { month })
      }
    },
    data => data.debts.push(row)
  );
}

export function removeDebt(id) {
  const removed = state.data?.debts.find(r => r.id === id);
  local(data => { data.debts = data.debts.filter(r => r.id !== id); });
  if (removed) rememberDeletion('debt', removed, row => addDebt(row));
  if (isTemp(id) && dropQueuedCreate(id)) return notify();
  mutate({ type: 'deleteDebt', targetId: id }, () => {});
}

/**
 * Mirrors the server: the balance of the month on screen falls, every later
 * month with it, and a Debt service line is posted in that month.
 */
export function payDebt(id, amount, { month = state.month, offerUndo = true } = {}) {
  const debt = state.data?.debts.find(d => d.id === id);
  if (!debt) return;

  const paid = amount > 0 ? amount : debt.payment;
  if (!paid) return;
  /* Never more than was owed that month -- the server clamps it the same way. */
  const applied = Math.min(paid, debtOwedIn(debt, month));
  if (!(applied > 0)) return;

  const expenseId = tempId();
  mutate(
    { type: 'payDebt', collection: 'expenses', tempId: expenseId, targetId: id, payload: { amount: paid, month } },
    data => {
      const target = data.debts.find(d => d.id === id);
      if (target) {
        target.history = debtHistoryWithPayment(target, month, applied);
        target.balance = debtLatestBalance(target);
      }
      data.expenses.push({
        id: expenseId, month, label: 'Debt service', amount: applied,
        // Paid in the currency the debt is denominated in, as the server does.
        currency: debt.currency,
        note: debt.name, origin: 'debt_payment', originId: null, debtId: id
      });
    }
  );

  /*
   * Logging a payment changed two figures and said nothing, so a second click
   * -- the first looked as if it had not worked -- paid the debt twice. It now
   * says it was done, and for a few seconds it can be taken back.
   */
  if (offerUndo) {
    rememberDeletion('payment', { expenseId, name: debt.name }, row => {
      if (state.data?.expenses.some(line => line.id === row.expenseId)) {
        removeExpense(row.expenseId, { offerUndo: false });
      }
    });
  }
}

export function addHolding({
  ticker, name, cls, units, cost, price, currency = entryCurrency(), since = state.month,
  yieldPct = 0, boughtOn = null, metalForm = null, unit = null, purity = null
}) {
  const id = tempId();
  const row = {
    id,
    ticker: String(ticker || 'NEW').toUpperCase(),
    name: name || ticker,
    cls: cls || 'Equity / ETF',
    units,
    cost: cost || price,
    price,
    currency,
    /** What a cash position earns, annually. Zero for everything else. */
    yieldPct: Number(yieldPct) || 0,
    /** The day it was bought, when the person gave one. */
    boughtOn: boughtOn || null,
    /** Precious metals only: paper or physical, and a physical one's unit (prices stay per ounce). */
    metalForm: metalForm || null,
    unit: unit || null,
    /** Fineness of physical metal, 0..1; null is pure. */
    purity: purity || null,
    /*
     * The month on screen, not today. Somebody entering a position they bought
     * in July is looking at July while they do it — and a position dated later
     * than it was held drops out of the months it belonged to.
     */
    since
  };
  mutate(
    { type: 'addHolding', collection: 'holdings', tempId: id, payload: { ...row, id: undefined } },
    data => data.holdings.push(row)
  );
}

/** Corrects the month a position starts counting from. */
export function setHoldingSince(id, since) {
  mutate(
    { type: 'updateHolding', targetId: id, payload: { since } },
    data => {
      const row = data.holdings.find(h => h.id === id);
      if (row) row.since = since;
    }
  );
}

/*
 * One row at a time, whatever kind of row it is.
 *
 * Every editable thing used to want its own pair of state fields and its own
 * pair of actions, and there are eight of them — holdings, debts, income,
 * expenses, fill-ups, vehicle charges, trades and recurring templates. Sixteen
 * fields describing "which one is open" is sixteen chances for two to be open
 * at once, which is exactly what happened the first time two dialogs were
 * added: they rendered stacked on top of each other.
 */
const EDIT_OPS = {
  holding: { op: 'updateHolding', find: data => data.holdings },
  debt: { op: 'updateDebt', find: data => data.debts },
  income: { op: 'updateIncome', find: data => data.income },
  expense: { op: 'updateExpense', find: data => data.expenses },
  fuel: { op: 'updateFuel', find: data => data.fuel },
  carEx: { op: 'updateCarExpense', find: data => data.carEx },
  recurring: { op: 'updateRecurring', find: data => data.recurring }
};

/**
 * Opens one row for correction.
 *
 * A holding also carries its class into state, because that dialog changes
 * shape with it — a balance has an amount and a rate where a share has a
 * quantity and a price — and an uncontrolled select cannot tell the renderer
 * that on its own.
 */
export function askEdit(kind, id) {
  const holding = kind === 'holding' ? state.data?.holdings?.find(h => h.id === id) : null;
  state.editing = { kind, id };
  state.editClass = holding ? holding.cls : null;
  /*
   * Dated fields are pickers, not inputs, so their value cannot live in the
   * DOM the way every other field's does. Cleared on open: a date left behind
   * from the last row edited would silently become this row's answer.
   */
  state.editDates = {};
  state.editFuelFull = null;
  /*
   * And the calendar itself is closed.
   *
   * A picker is open when `state.monthPicker` holds its id, and the id is
   * derived from the field name -- so the same field in the next row opened has
   * the same id. Leaving a dialog with the calendar showing left that id behind,
   * and the next row edited opened with its form already covered by a calendar
   * nobody asked for.
   */
  state.monthPicker = null;
  state.monthPickerYear = null;
  state.datePickerMonth = null;
  notify();
}

export function closeEdit() {
  state.editDates = {};
  state.editFuelFull = null;
  /* Leaving a dialog closes its calendar with it -- see `askEdit`. */
  state.monthPicker = null;
  state.monthPickerYear = null;
  state.datePickerMonth = null;
  state.editing = null;
  state.editClass = null;
  notify();
}

export function setEditClass(cls) {
  state.editClass = cls;
  notify();
}

/**
 * Writes a corrected row back, whatever kind it is.
 *
 * The whole row at once rather than a field at a time, because the fields are
 * one figure read several ways: changing a quantity changes what a position
 * cost, changing a fill-up's odometer changes the economy of the fill after it.
 * Saving them separately would leave the ledger briefly reporting a state that
 * was never true.
 */
/** What a dialog's calendar has been set to. Empty string means "cleared". */
export function setEditDate(name, value) {
  state.editDates = { ...(state.editDates || {}), [name]: value || '' };
  notify();
}

/** The switch beside the fill-up form: on means this fill did not fill the tank. */
export function setFuelPartial(partial) {
  state.fuelPartial = Boolean(partial);
  notify();
}

export function setEditFuelFull(full) {
  state.editFuelFull = Boolean(full);
  notify();
}

export function saveEdit(kind, id, patch) {
  const spec = EDIT_OPS[kind];
  if (!spec) return;

  /*
   * A debt's balance is a statement about the month on screen, like every
   * other figure in the ledger, and the server files it there. The rest of a
   * debt has no months and is simply replaced.
   */
  const month = state.month;
  const dated = kind === 'debt' && patch.balance !== undefined;

  mutate(
    { type: spec.op, targetId: id, payload: dated ? { ...patch, month } : patch },
    data => {
      const row = (spec.find(data) || []).find(item => item.id === id);
      if (!row) return;
      if (!dated) return Object.assign(row, patch);

      const { balance, ...rest } = patch;
      Object.assign(row, rest);
      row.history = debtHistoryWithBalance(row, month, balance);
      row.balance = debtLatestBalance(row);
    }
  );
  closeEdit();
}

/**
 * The journal writes straight through instead of queueing.
 *
 * It sits outside the ledger by design and keeps its own copy, so there is no
 * offline queue to put this in and nothing else to keep it consistent with.
 */
export async function saveTradeEdit(id, patch) {
  closeEdit();
  await updateTrade(id, patch);
}

/**
 * Closes a position from a month on, without touching the months it was held.
 *
 * This is what selling means, and it is what the delete button used to do
 * wrongly: deleting the row took the holding out of every month it had ever
 * been in, so a sale in August silently rewrote the trend back to the purchase.
 *
 * Passing null re-opens it, which is the undo for having pressed sell.
 */
export function setHoldingClass(cls) {
  state.holdingClass = cls;
  notify();
}

export function askHoldingExit(id) {
  state.holdingExit = id;
  notify();
}

export function closeHoldingExit() {
  state.holdingExit = null;
  notify();
}

export function sellHolding(id, until) {
  mutate(
    { type: 'updateHolding', targetId: id, payload: { until } },
    data => {
      const row = data.holdings.find(h => h.id === id);
      if (row) row.until = until;
    }
  );
  state.holdingExit = null;
  notify();
}

export function removeHolding(id) {
  state.holdingExit = null;
  const removed = state.data?.holdings.find(r => r.id === id);
  local(data => { data.holdings = data.holdings.filter(r => r.id !== id); });
  if (removed) rememberDeletion('holding', removed, row => addHolding(row));
  if (isTemp(id) && dropQueuedCreate(id)) return notify();
  mutate({ type: 'deleteHolding', targetId: id }, () => {});
}

/**
 * Renames the vehicle or corrects its odometer.
 *
 * Takes the units the reader sees and converts here, like every other vehicle
 * entry — the form should not have to know what the storage unit is.
 */
export function updateVehicle({ name, odo, startOdo, purchasePrice, purchaseDate, tank, vin, serviceInterval }) {
  const patch = {};
  if (name !== undefined) patch.name = name;
  if (odo !== undefined) patch.odo = toCanonicalDistance(odo);
  if (startOdo !== undefined) patch.startOdo = toCanonicalDistance(startOdo);
  // Money and dates need no unit conversion; they pass straight through.
  if (purchasePrice !== undefined) patch.purchasePrice = purchasePrice;
  if (purchaseDate !== undefined) patch.purchaseDate = purchaseDate;
  /* Litres on the wire and in storage, like every volume; null clears it. */
  if (tank !== undefined) patch.tank = tank === null ? null : toCanonicalVolume(tank);
  if (vin !== undefined) patch.vin = vin;
  /* The oil-change interval, in the reader's distance unit; kilometres on the wire. */
  if (serviceInterval !== undefined) {
    patch.serviceKm = serviceInterval === null ? null : Math.round(toCanonicalDistance(serviceInterval));
  }

  mutate(
    { type: 'updateVehicle', payload: patch },
    data => { data.vehicle = { ...data.vehicle, ...patch }; }
  );
  /* Picking an interval from the chips is its own confirmation; a toast would only repeat it. */
  if (Object.keys(patch).some(key => key !== 'serviceKm')) state.notice = 'vehicle:saved';
  else notify();
}

/**
 * Converts the vehicle figures a person typed into the canonical units.
 *
 * Every conversion lives here rather than in the forms: two layouts entering
 * the same reading through two code paths is exactly how one of them ends up
 * writing miles into a kilometre column.
 */
function toCanonicalDistance(value) {
  return state.settings.unitSystem === 'imperial' ? milesToKm(value) : value;
}

function toCanonicalVolume(value) {
  return state.settings.unitSystem === 'imperial' ? gallonsToLitres(value) : value;
}

/* The inverses, for putting a stored row back through the entry path. */
function fromCanonicalDistance(value) {
  return state.settings.unitSystem === 'imperial' ? kmToMiles(value) : value;
}

function fromCanonicalVolume(value) {
  return state.settings.unitSystem === 'imperial' ? litresToGallons(value) : value;
}

/**
 * @param date  'YYYY-MM-DD', defaulting to today.
 *
 * Both vehicle forms used to stamp `new Date()` unconditionally, so entering a
 * fill-up or a service from last spring filed it under today — the month it
 * belonged to never saw it, and the odometer gaps that drive fuel economy were
 * computed against the wrong ordering.
 */
export function addFuel({
  odo: enteredOdo, gal: enteredVolume, cost, currency = entryCurrency(), date: on, full = true
}) {
  const id = tempId();
  const date = isDate(on) ? on : localDay();
  const odo = toCanonicalDistance(enteredOdo);
  const gal = toCanonicalVolume(enteredVolume);

  mutate(
    { type: 'addFuel', collection: 'fuel', tempId: id, payload: { date, odo, gal, cost, currency, full } },
    data => {
      data.fuel.push({ id, date, odo, gal, cost, currency, full });
      if (odo > data.vehicle.odo) data.vehicle.odo = odo;
      if (cost > 0) {
        data.expenses.push({
          id: tempId(), month: date.slice(0, 7), label: 'Vehicle', amount: cost, currency,
          note: 'fuel', origin: 'fuel', originId: id
        });
      }
    }
  );
}

export function removeFuel(id) {
  const removed = state.data?.fuel.find(r => r.id === id);
  local(data => {
    data.fuel = data.fuel.filter(r => r.id !== id);
    data.expenses = data.expenses.filter(e => !(e.origin === 'fuel' && e.originId === id));
  });
  if (removed) {
    // Restoring re-enters the fill-up, which re-posts its Vehicle expense too —
    // the same path the original entry took, so nothing can drift apart.
    /*
     * The date and the flag come back too. Undo used to re-enter the fill
     * without its date, so a restored fill from last month landed on today --
     * in the wrong month's budget, and out of order for consumption.
     */
    rememberDeletion('fuel', removed, row => addFuel({
      odo: fromCanonicalDistance(row.odo),
      gal: fromCanonicalVolume(row.gal),
      cost: row.cost,
      currency: row.currency,
      date: row.date,
      full: row.full !== false
    }));
  }
  if (isTemp(id) && dropQueuedCreate(id)) return notify();
  mutate({ type: 'deleteFuel', targetId: id }, () => {});
}

/** @param date  'YYYY-MM-DD', defaulting to today — see {@link addFuel}. */
/** `odo` is already in kilometres -- the form converts, so a restored row is not converted twice. */
export function addCarExpense({ type, note, amount, currency = entryCurrency(), date: on, odo = null, oilChange = false }) {
  const id = tempId();
  const date = isDate(on) ? on : localDay();
  const reading = odo > 0 ? odo : null;
  mutate(
    { type: 'addCarExpense', collection: 'carEx', tempId: id, payload: { date, type, note, amount, currency, odo: reading, oilChange: Boolean(oilChange) } },
    data => {
      data.carEx.push({ id, date, type, note: note || '', amount, currency, odo: reading, oilChange: Boolean(oilChange) });
      data.expenses.push({
        id: tempId(), month: date.slice(0, 7), label: 'Vehicle', amount, currency,
        note: type, origin: 'car_expense', originId: id
      });
    }
  );
}

export function removeCarExpense(id) {
  const removed = state.data?.carEx.find(r => r.id === id);
  local(data => {
    data.carEx = data.carEx.filter(r => r.id !== id);
    data.expenses = data.expenses.filter(e => !(e.origin === 'car_expense' && e.originId === id));
  });
  if (removed) rememberDeletion('service', removed, row => addCarExpense(row));
  if (isTemp(id) && dropQueuedCreate(id)) return notify();
  mutate({ type: 'deleteCarExpense', targetId: id }, () => {});
}


/**
 * Fills the price history in with what each holding was actually worth.
 *
 * The counterpart to a refresh: that asks what a holding is worth now, this
 * asks what it was worth in each of the last twelve months, so scrolling back
 * shows the portfolio as it stood rather than as it stands.
 */
export async function backfillPrices() {
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const result = await api.backfillPrices();
    await refresh({ silent: true });

    if (!result.filled) {
      state.notice = result.missed?.length
        ? `prices:backfillMissed:${result.missed.join(', ')}`
        : 'prices:backfillNone';
    } else {
      state.notice = `prices:backfilled:${result.filled}`;
    }
    return result;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

/** Marks holdings to market. Needs the network, so it is not queued. */
export async function refreshPrices() {
  state.busy = true;
  notify();
  try {
    const result = await api.refreshPrices();
    await refresh({ silent: true });

    /*
     * Three things can go other than perfectly, and only one of them used to be
     * reported.
     *
     * A refused ticker is in neither `prices` nor `missed`, so the toast said
     * "updated" while nothing had been written for it. And a converted price
     * changes by whatever the exchange rate is -- silver went from 67.79 to
     * 345.71 in one press -- which is the sort of thing a person is entitled to
     * be told about their own figures.
     */
    const refused = (result.mismatched || []).map(row => row.ticker);
    const converted = (result.converted || []).length;

    if (refused.length) {
      state.notice = `prices:noRate:${refused.join(', ')}`;
    } else if (result.missed?.length) {
      state.notice = `prices:partial:${result.missed.join(', ')}`;
    } else if (converted) {
      state.notice = `prices:converted:${converted}`;
    } else {
      state.notice = 'prices:ok';
    }
    return result;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * Fills the rate history in with what each month was actually worth.
 *
 * Separate action from `refreshRates` because it answers a different question:
 * that one asks what a currency is worth now, this one asks what it was worth
 * then. Both need the network, so neither is queued.
 */
export async function backfillRates() {
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const result = await api.backfillRates();
    await refresh({ silent: true });

    if (!result.filled) {
      state.notice = result.missed?.length
        ? `rates:backfillMissed:${result.missed.join(', ')}`
        : 'rates:backfillNone';
    } else {
      state.notice = `rates:backfilled:${result.filled}`;
    }
    return result;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

// ---------------------------------------------------------- Liquidity+ -----

/**
 * Reads, and takes out of the address bar, what Stripe sent the browser back
 * with: `?billing=success&session_id=cs_...` or `?billing=cancel`. Taken out
 * so a reload does not announce the same payment twice.
 */
function takeBillingParams() {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }
  const result = params.get('billing');
  if (result !== 'success' && result !== 'cancel' && result !== 'portal') return null;
  const sessionId = params.get('session_id');

  params.delete('billing');
  params.delete('session_id');
  const query = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
  return { result, sessionId };
}

/**
 * Says how the trip to Checkout ended, with the account already up to date.
 *
 * The server asks Stripe about the session rather than waiting for the
 * webhook, so "Liquidity+ is active" is true when it is said. A payment still
 * being processed -- some methods take a while -- is said to be exactly that.
 */
async function settleBillingReturn({ result, sessionId }) {
  /* Back from the portal: whatever was changed there, show it now. Quietly. */
  if (result === 'portal') {
    try {
      const answer = await api.billingRefresh();
      if (answer.user) state.user = answer.user;
    } catch {
      /* The webhook will bring the same news shortly. */
    }
    notify();
    return;
  }
  if (result === 'cancel' || !sessionId) {
    state.notice = 'billing:cancel';
    notify();
    return;
  }
  try {
    const answer = await api.billingSync(sessionId);
    if (answer.user) state.user = answer.user;
    state.notice = answer.user?.plan?.billing?.subscription ? 'billing:success' : 'billing:pending';
  } catch (error) {
    state.error = errorKey(error);
  }
  notify();
}

/*
 * Coming back from Stripe with the browser's Back button restores this page
 * from memory, busy flag and all -- which would leave every button dead.
 */
function releaseOnReturn() {
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      state.busy = false;
      notify();
    }
  }, { once: true });
}

/** Sends the browser to Stripe Checkout for a monthly or a yearly Liquidity+. */
/** Which prices to show and sell: the visitor's pick on this device, else the time zone's region. */
export function setPriceRegion(region, currency) {
  rememberRegion(region, currency);
  state.priceRegion = { region, currency, picked: true };
  notify();
}

export async function startCheckout(interval) {
  if (state.busy) return;
  releaseOnReturn();
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const region = state.priceRegion || priceRegion();
    const { url } = await api.billingCheckout(interval, region.region, region.currency);
    window.location.assign(url);
  } catch (error) {
    state.error = errorKey(error);
    state.busy = false;
    notify();
  }
}

/** Opens Stripe's customer portal: the card, invoices, cancelling. */
export async function openBillingPortal() {
  if (state.busy) return;
  releaseOnReturn();
  state.busy = true;
  state.error = null;
  notify();
  try {
    const { url } = await api.billingPortal();
    window.location.assign(url);
  } catch (error) {
    state.error = errorKey(error);
    state.busy = false;
    notify();
  }
}

// ------------------------------------------------------ model provider -----

export async function loadAiSettings({ force = false } = {}) {
  if (state.ai && !force) return state.ai;
  try {
    state.ai = await api.aiSettings();
    notify();
    return state.ai;
  } catch {
    // Nothing here is load-bearing; the panel simply shows it as unconfigured.
    return null;
  }
}

/**
 * Asks the model to sort the statement's descriptions.
 *
 * Nothing is written: the answers land on the review screen as proposals, on
 * the rows the person is already looking at, and the import button they were
 * always going to press is what commits them. Rows the model was unsure about
 * are left alone rather than filled with a guess.
 */
export async function categoriseImport() {
  const draft = state.importing;
  if (!draft) return null;

  /*
   * Only what nothing else recognised. Rows the file, the account's own rules
   * or the dictionary already settled are not asked about: those are the ones
   * the model got wrong most often, and every row asked is paid for.
   */
  const rows = draft.entries.filter(
    entry => entry.valid && entry.kind === 'expense' && !entry.categorySource
  );
  if (!rows.length) {
    const anyExpense = draft.entries.some(entry => entry.valid && entry.kind === 'expense');
    state.notice = anyExpense ? 'ai:allset' : 'ai:nothing';
    notify();
    return null;
  }

  /*
   * One question per payee, cleaned of card numbers and dates. Twelve visits to
   * the same shop are one line to the model, which also means they cannot come
   * back with two different answers.
   */
  const limit = state.ai?.maxLabels || 300;
  const questions = [];
  const seen = new Map();
  const asks = rows.map(row => {
    const text = cleanDescription([row.label, row.note].filter(Boolean).join(' ')).slice(0, 200);
    if (!seen.has(text) && questions.length < limit) {
      seen.set(text, questions.length);
      questions.push(text);
    }
    return seen.has(text) ? seen.get(text) : null;
  });

  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const result = await api.categorise(questions);
    const answers = result.categories || {};
    /* The month's allowance on the service key, as the server now counts it. */
    if (result.server && state.ai?.server) {
      state.ai.server = { ...state.ai.server, used: state.ai.server.limit - result.left };
    }

    let applied = 0;
    rows.forEach((entry, index) => {
      const answer = asks[index] === null ? null : answers[asks[index]];
      if (answer === TRANSFER) {
        entry.transfer = true;
        entry.category = null;
      } else if (CATEGORIES.includes(answer)) {
        entry.category = answer;
      } else {
        return;
      }
      entry.categorySource = 'ai';
      entry.include = decideInclude(draft, entry);
      applied += 1;
    });

    /*
     * The search count travels with the result.
     *
     * Without it there is no way to tell a model that searched and found
     * nothing from a feature that never ran — and "is it actually searching?"
     * is unanswerable from the outside, which is exactly the question the rows
     * it leaves blank provoke.
     */
    state.notice = `ai:sorted:${applied}:${rows.length}:${result.searches || 0}`;
    return result;
  } catch (error) {
    /*
     * A code, translated here — the provider's own JSON reached the screen once
     * as `401 {"type":"error"...}`, which tells the reader nothing they can do.
     */
    state.error = error instanceof OfflineError
      ? 'offline'
      : (String(error.code || '').startsWith('ai_') ? error.code : error.message);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

// ------------------------------------------------------------- csv import ---

/** How many rows the review table shows before it stops being readable. */
const IMPORT_PREVIEW = 60;

/**
 * Decodes a statement whatever the bank saved it as.
 *
 * Polish banks still export Windows-1250, and reading one as UTF-8 turns every
 * description into rubble. UTF-8 is tried strictly first -- it fails loudly on
 * bytes that are not valid UTF-8, which is exactly the signal needed -- and the
 * fallback is the encoding those exports actually use.
 */
function decodeStatement(bytes) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1250').decode(bytes);
  }
}

/** The account's own corrections, as the lookup the classifier takes. */
const categoryRules = () => new Map(
  (state.data?.categoryRules || []).map(rule => [rule.merchant, rule.category])
);

/**
 * Whether a row goes in: readable, not unticked, and -- for money moved between
 * the person's own accounts -- only when it was ticked back in by hand.
 */
const decideInclude = (draft, entry) => entry.valid
  && !draft.skipped.has(entry.line)
  && (!entry.transfer || draft.kept.has(entry.line))
  /* Already in the ledger: left out unless ticked back in, like a transfer. */
  && (!draft.duplicates?.has(entry.line) || draft.kept.has(entry.line))
  /* A payment the bank reverted or declined: no money moved, so it stays out unless ticked in. */
  && (!entry.voided || draft.kept.has(entry.line));

/** The rows as the server reads them -- the same shape confirmImport sends. */
const importPayload = (draft, rows) => rows.map(({ line, kind, month, label, amount, currency, category, note }) => ({
  line, kind, label, amount, currency, category, note,
  month: draft.monthMode === 'dated' ? month : draft.month
}));

/*
 * Asks the server which rows it already holds, so a statement imported twice
 * shows them as already there -- unticked -- instead of offering all of them
 * and answering "0 imported, 5 already there" only after the click. A failed
 * check changes nothing: the import itself still skips duplicates.
 */
async function checkImportDuplicates() {
  const draft = state.importing;
  if (!draft) return;
  const rows = draft.entries.filter(entry => entry.valid);
  if (!rows.length) return;
  const asked = draft.monthMode;
  try {
    const { duplicates } = await api.checkImport(importPayload(draft, rows));
    if (state.importing !== draft || draft.monthMode !== asked) return;
    draft.duplicates = new Set(duplicates);
    for (const entry of draft.entries) entry.include = decideInclude(draft, entry);
    notify();
  } catch {
    /* Offline or refused: the server still skips them when the import is sent. */
  }
}

/** What can be said about a row without asking anybody -- see shared/merchants.js. */
const settle = (entry, rules) => {
  const found = classify(entry, rules);
  entry.category = found.category;
  entry.categorySource = found.source;
  entry.transfer = found.transfer;
};

/* Whether this browser writes dates month first (en-US and the few like it), asked of the browser itself. */
function monthFirstLocale() {
  try {
    const parts = new Intl.DateTimeFormat(navigator.language || 'en-GB').formatToParts(new Date(2026, 9, 25));
    const order = parts.filter(part => part.type === 'day' || part.type === 'month').map(part => part.type);
    return order[0] === 'month';
  } catch {
    return false;
  }
}

const rebuildImport = draft => {
  const before = new Map((draft.entries || []).map(entry => [entry.line, entry]));
  draft.entries = rowsToEntries(draft.rows, draft.mapping, {
    defaultCurrency: state.settings.baseCurrency,
    from: draft.headerRow + 1,
    lines: draft.lines,
    /* For a file whose dates never say (every day 12 or under): month first where the browser writes dates that way. */
    dateOrder: monthFirstLocale() ? 'mdy' : 'dmy'
  });

  const rules = categoryRules();
  draft.entries.forEach(entry => {
    /*
     * A category chosen on this screen survives a remapping -- re-mapping a
     * column is not a reason to ask a paid API the same question twice. Only
     * while the row still reads the same, though: a different description
     * column is a different question.
     *
     * (It never did survive before: the entries were replaced before the old
     * ones were read, so every remapping silently threw the answers away.)
     */
    const earlier = before.get(entry.line);
    if (earlier && (earlier.categorySource === 'manual' || earlier.categorySource === 'ai')
        && earlier.label === entry.label && earlier.kind === entry.kind) {
      entry.category = earlier.category;
      entry.categorySource = earlier.categorySource;
      entry.transfer = earlier.transfer;
    } else {
      settle(entry, rules);
    }
    // A row the user unticked stays unticked across a remapping.
    entry.include = decideInclude(draft, entry);
  });
  return draft;
};

/**
 * Reads a statement and opens it for review.
 *
 * Nothing is written yet, and nothing leaves the device: this only turns the
 * file into rows so the column mapping can be corrected before it matters.
 */
export async function openImport(file) {
  if (!file) return;
  state.error = null;
  state.errorDetail = null;

  let text;
  try {
    text = decodeStatement(new Uint8Array(await file.arrayBuffer()));
  } catch {
    state.error = 'import:unreadable';
    return notify();
  }

  const { rows, lines } = readCsv(text);
  const headerRow = findHeaderRow(rows);
  if (rows.length < headerRow + 2) {
    state.error = 'import:empty';
    return notify();
  }

  // Cheap and memoised: a session open since before the key was stored would
  // otherwise carry a stale "not configured" until the next reload.
  loadAiSettings();

  state.importing = rebuildImport({
    name: file.name || 'statement.csv',
    rows,
    lines,
    headerRow,
    header: rows[headerRow],
    mapping: guessColumns(rows[headerRow]),
    skipped: new Set(),
    /* Rows ticked back in by hand -- a transfer the importer left out, included anyway. */
    kept: new Set(),
    /*
     * The month the rows go into: the one open in the app, like everything else
     * added while looking at a month. 'dated' files each row by its own date
     * instead, for a statement that covers several months.
     */
    month: state.month,
    monthMode: 'viewed',
    preview: IMPORT_PREVIEW,
    entries: [],
    result: null,
    duplicates: new Set()
  });
  notify();
  checkImportDuplicates();
}

/** Points one field at a different column, or at none. */
export function setImportColumn(field, index) {
  if (!state.importing) return;
  state.importing.mapping = {
    ...state.importing.mapping,
    [field]: index === '' || index === null ? null : Number(index)
  };
  rebuildImport(state.importing);
  notify();
  checkImportDuplicates();
}

/** Flips one row's kind by hand, for the transfers a sign gets wrong. */
export function setImportKind(line, kind) {
  const draft = state.importing;
  const entry = draft?.entries.find(row => row.line === line);
  if (!entry) return;
  entry.kind = kind;
  /* Income has no category and an expense needs one: read the row again as what it now is. */
  if (entry.categorySource !== 'manual') settle(entry, categoryRules());
  entry.include = decideInclude(draft, entry);
  notify();
}

/**
 * Corrects one row's category -- and every other row paid to the same payee
 * that nobody has corrected by hand, so twelve visits to one shop are fixed
 * once. What was chosen here is remembered once the import is confirmed; see
 * `confirmImport`.
 */
export function setImportCategory(line, category) {
  const draft = state.importing;
  const entry = draft?.entries.find(row => row.line === line);
  if (!entry || !CATEGORIES.includes(category)) return;

  const assign = (row, source) => {
    row.category = category;
    row.categorySource = source;
    /* Giving a row a category says it was spending, not a transfer. */
    row.transfer = false;
    row.include = decideInclude(draft, row);
  };

  assign(entry, 'manual');
  const key = merchantKey(payeeOf(entry));
  if (key) {
    draft.entries.forEach(row => {
      if (row === entry || !row.valid || row.kind !== 'expense') return;
      if (row.categorySource === 'manual') return;
      if (merchantKey(payeeOf(row)) === key) assign(row, 'rule');
    });
  }
  notify();
}

/** 'viewed' files every row in the month open in the app; 'dated' by each row's own date. */
export function setImportMonthMode(mode) {
  if (!state.importing || (mode !== 'viewed' && mode !== 'dated')) return;
  state.importing.monthMode = mode;
  notify();
  /* Another month is another ledger to compare against. */
  checkImportDuplicates();
}

/** Lifts the preview limit: correcting categories means seeing every row. */
export function showAllImportRows() {
  if (!state.importing) return;
  state.importing.preview = state.importing.entries.length;
  notify();
}

export function toggleImportRow(line) {
  const draft = state.importing;
  const entry = draft?.entries.find(row => row.line === line);
  if (!entry || !entry.valid) return;

  entry.include = !entry.include;
  /* Both directions are remembered, so a remapping keeps them -- see `decideInclude`. */
  if (entry.include) {
    draft.skipped.delete(line);
    draft.kept.add(line);
  } else {
    draft.skipped.add(line);
    draft.kept.delete(line);
  }
  notify();
}

/**
 * One rule per payee whose category was chosen by hand on the review screen:
 * what the next statement will be sorted by before anybody is asked.
 */
function learnedRules(draft) {
  const byMerchant = new Map();
  draft.entries.forEach(entry => {
    if (entry.categorySource !== 'manual' || entry.kind !== 'expense' || !entry.category) return;
    const merchant = merchantKey(payeeOf(entry));
    if (merchant) byMerchant.set(merchant, entry.category);
  });
  return [...byMerchant].map(([merchant, category]) => ({ merchant, category }));
}

export function closeImport() {
  state.importing = null;
  notify();
}

/**
 * Writes the approved rows.
 *
 * Not queued. An import is hundreds of rows at once, and replaying that from a
 * stale offline queue days later is exactly how a ledger gets a duplicate month
 * nobody notices.
 */
export async function confirmImport() {
  markGuestTouched();
  const draft = state.importing;
  if (!draft) return null;

  const entries = draft.entries
    .filter(entry => entry.include)
    /*
     * The suggested category travels beside the description, never instead of
     * it: the server files the row under the category and keeps the shop name
     * as the note, so "LIDL 2073" survives being called Groceries.
     */
    /* `note` travels too: the file's own note column, which is what tells two
       otherwise identical rows apart — see the import route. */
    .map(({ line, kind, month, label, amount, currency, category, note }) => ({
      line, kind, label, amount, currency, category, note,
      month: draft.monthMode === 'dated' ? month : draft.month,
      /* A row the review marked as already there, ticked back in on purpose: import it anyway. */
      ...(draft.duplicates?.has(line) && draft.kept.has(line) ? { force: true } : {})
    }));

  if (!entries.length) {
    state.error = 'import:nothing';
    notify();
    return null;
  }

  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const result = await api.importEntries(entries);
    /* The free plan's counter moves with the import that used it. */
    if (result.imported && state.user?.plan?.used) state.user.plan.used.csvImports += 1;
    /*
     * What was corrected by hand is remembered for the next statement. After
     * the import rather than before, so a review abandoned half-way teaches
     * nothing -- and a failure here costs the memory, never the import that
     * has already landed.
     */
    const rules = learnedRules(draft);
    if (rules.length) {
      try {
        await api.saveCategoryRules(rules);
      } catch {
        /* Nothing to undo: the rows are in, and the next correction tries again. */
      }
    }
    await refresh({ silent: true });
    state.importing = null;

    /*
     * Land on the month the statement was for.
     *
     * A July statement imported while the dashboard shows August writes every
     * row correctly and changes nothing you can see — which reads as "the
     * import did nothing" and is the single most confusing thing this feature
     * could do. Follow the entries instead of making the reader find them.
     */
    const months = entries.map(entry => entry.month).filter(Boolean).sort();
    if (months.length && !months.includes(state.month)) {
      const busiest = months.reduce((best, month, unused, all) => {
        const count = all.filter(m => m === month).length;
        return count > best.count ? { month, count } : best;
      }, { month: months[0], count: 0 });

      state.month = busiest.month;
      persistUi();
      recompute();
    }

    state.notice = `import:done:${result.imported}:${result.skipped}:${result.duplicates || 0}`;
    return result;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * Writes the cashflow back out in the same shape it is read in.
 *
 * Deliberately round-trippable: an export you cannot import is a screenshot
 * with extra steps.
 */
export function exportCsv() {
  const data = state.data;
  if (!data) return null;

  const base = state.settings.baseCurrency;

  // Money out is negative, the way a bank writes it, so the file reads back in.
  const line = sign => row => [
    row.month,
    row.label,
    (sign * Number(row.amount)).toFixed(2),
    row.currency || base,
    row.note || ''
  ];

  const body = [
    ...(data.income || []).map(line(1)),
    ...(data.expenses || []).map(line(-1))
  ].sort((a, b) => String(a[0]).localeCompare(String(b[0])));

  const header = ['date', 'description', 'amount', 'currency', 'note'];
  return { text: toCsv([header, ...body]), rows: body.length };
}

// ------------------------------------------------------------ connectivity --

window.addEventListener('online', () => {
  state.online = true;
  notify();
  flush().then(() => refresh({ silent: true }));
});

window.addEventListener('offline', () => {
  state.online = false;
  notify();
});


// ------------------------------------------------------- trading journal ----

/*
 * The journal never touches the ledger.
 *
 * A prop-firm balance is not money you own, and folding an open P&L into net
 * worth would make that figure swing on positions that may be closed by
 * lunchtime. A payout, when one lands, is entered as income like any other.
 *
 * These calls are not queued either: they are not offline-first, and replaying
 * a trade entry from a stale queue days later would put a fictional row in a
 * record whose whole value is that it says what actually happened.
 */

export async function loadTrades({ force = false } = {}) {
  if (state.trades && !force) return state.trades;
  if (state.tradesLoading) return state.trades;
  // A failed attempt is not retried by a repaint; see `tradesFailed`.
  if (state.tradesFailed && !force) return null;

  state.tradesLoading = true;
  state.tradesFailed = false;
  notify();
  try {
    /*
     * The sessions come with the trades: a trade's session is only a name once
     * the list is there. A failure to list them costs the backtest half, not
     * the journal.
     */
    const [trades, backtests] = await Promise.all([api.trades(), api.backtests().catch(() => [])]);
    state.trades = trades;
    state.backtests = backtests;
    return state.trades;
  } catch (error) {
    state.tradesFailed = true;
    state.error = errorKey(error);
    return null;
  } finally {
    state.tradesLoading = false;
    notify();
  }
}

// ------------------------------------------------------------ backtests ---

// --------------------------------------------------------------- review ---

export async function loadReview() {
  if (state.reviewLoading || state.review) return;
  state.reviewLoading = true;
  try {
    state.review = await api.review();
  } catch {
    state.review = { offered: false, available: false, review: null, nextAt: null };
  } finally {
    state.reviewLoading = false;
    notify();
  }
}

/** Asks Claude for this week's review -- only ever on this press. */
export async function requestReview() {
  if (state.reviewLoading) return;
  state.reviewLoading = true;
  state.error = null;
  notify();
  try {
    const result = await api.writeReview();
    state.review = { ...(state.review || {}), offered: true, available: true, ...result };
  } catch (error) {
    state.error = errorKey(error);
  } finally {
    state.reviewLoading = false;
    notify();
  }
}

export function setWhatIf(key, value) {
  state.whatIf = { ...state.whatIf, [key]: Number(value) || 0 };
  persistUi();
  notify();
}

/** A preset: the three amounts at once (see WHAT_IF_PRESETS in views/common.js). */
export function setWhatIfPreset(values) {
  state.whatIf = { ...state.whatIf, ...values };
  state.whatIfRev += 1;
  state.whatIfHover = null;
  persistUi();
  notify();
}

export function openWhatIf(open = true) {
  /* Which way the swap moves: the simulator comes in from the right, the overview back from the left. */
  state.whatIfFrom = open ? 1 : -1;
  state.whatIfOpen = Boolean(open);
  state.whatIfHover = null;
  notify();
  if (typeof window !== 'undefined' && window.scrollTo) window.scrollTo(0, 0);
}

export function setTrendHover(index) {
  const next = index === null || index === undefined ? null : index;
  if (next === state.trendHover) return;
  state.trendHover = next;
  notify();
}

export function setBacktestUi(patch) {
  state.backtestUi = { ...state.backtestUi, ...patch };
  notify();
}

export function setJournalUi(patch) {
  state.journalUi = { ...state.journalUi, ...patch };
  notify();
}

export function setVehUi(patch) {
  state.vehUi = { ...state.vehUi, ...patch };
  notify();
}

export function setInvUi(patch) {
  state.invUi = { ...state.invUi, ...patch };
  notify();
}

/**
 * What a metal cost in a month, per the holding's unit and in its currency --
 * the figure a physical purchase's cost is filled in from. Asked for from the
 * form as it renders, so the pending state is set without a repaint: the
 * render that asked must not ask again.
 */
export async function loadMetalPrice({ key, ticker, month, currency, unit }) {
  const current = state.invUi.metalQuote;
  if (current && current.key === key) return;
  state.invUi = { ...state.invUi, metalQuote: { key, status: 'pending' } };
  try {
    const row = await api.metalPrice({ ticker, month, currency, unit });
    if (state.invUi.metalQuote?.key !== key) return;
    state.invUi = { ...state.invUi, metalQuote: { key, status: 'ok', price: row.price, perOunce: row.perOunce, quoted: row.quoted } };
  } catch (error) {
    if (state.invUi.metalQuote?.key !== key) return;
    const code = errorKey(error);
    state.invUi = { ...state.invUi, metalQuote: { key, status: /rate_missing/.test(String(code)) ? 'rate' : 'missing' } };
  }
  notify();
}

export function setDebtUi(patch) {
  state.debtUi = { ...state.debtUi, ...patch };
  notify();
}

export function setDebtExtra(value) {
  state.debtExtra = Math.max(0, Number(value) || 0);
  persistUi();
  notify();
}

export function setCashUi(patch) {
  state.cashUi = { ...state.cashUi, ...patch };
  notify();
}

export function toggleUpcomingAll() {
  state.upcomingAll = !state.upcomingAll;
  notify();
}

/** Re-renders only when the month under the pointer changes. */
export function setWhatIfHover(month) {
  const next = month === null || month === undefined ? null : month;
  if (next === state.whatIfHover) return;
  state.whatIfHover = next;
  notify();
}

export function setCushionMonths(months) {
  state.cushionMonths = [3, 6, 12].includes(months) ? months : 6;
  persistUi();
  notify();
}

export function setPropTrader(on) {
  state.propTrader = Boolean(on);
  persistUi();
  notify();
}

export function setPitYear(year) {
  state.pitYear = year;
  state.pitConverted = null;
  persistUi();
  notify();
}

/** One figure typed into the PIT-38 form, for the year on screen. */
export function setPitInput(key, value) {
  const year = String(state.pitYear);
  state.pitInputs = { ...state.pitInputs, [year]: { ...(state.pitInputs[year] || {}), [key]: value } };
  if (key === 'journal') state.pitConverted = null;
  persistUi();
  notify();
}

/**
 * The year's real trades (never backtests), converted at the NBP rate of the
 * business day before each -- the one conversion PIT-38 accepts.
 */
export async function convertPitTrades() {
  if (state.pitLoading) return;
  const year = String(state.pitYear);
  /* Own-money trades only: see isTaxableTrade. */
  const trades = (state.trades || []).filter(row => isTaxableTrade(row) && String(row.date).startsWith(year));
  state.pitLoading = true;
  state.error = null;
  notify();
  try {
    const { items } = trades.length
      ? await api.taxConvert(trades.map(row => ({ currency: row.currency || 'PLN', date: row.date, amount: row.netPl })))
      : { items: [] };
    state.pitConverted = {
      year,
      /* Which trades these are, so the Taxes tab can tell when the journal has moved on. */
      fingerprint: tradesFingerprint(trades),
      items: items.map((item, i) => ({ ...item, pair: trades[i].pair }))
    };
  } catch (error) {
    state.error = errorKey(error);
  } finally {
    state.pitLoading = false;
    notify();
  }
}

/** Whose tax return the Taxes tab helps with. */
export function setTaxCountry(country) {
  state.taxCountry = country;
  persistUi();
  notify();
}

/**
 * One thing typed, picked or ticked for a country other than Poland, for the
 * year on screen: a field by its key, or `pick:<root>` for an instrument on
 * the US form.
 */
export function setTaxInput(country, key, value) {
  const year = String(state.pitYear);
  const byYear = state.taxInputs[country] || {};
  const saved = byYear[year] || {};
  const next = key.startsWith('pick:')
    ? { ...saved, picks: { ...(saved.picks || {}), [key.slice(5)]: Boolean(value) } }
    : { ...saved, [key]: value };
  state.taxInputs = { ...state.taxInputs, [country]: { ...byYear, [year]: next } };
  persistUi();
  notify();
}

let taxVotesLoading = false;

/** The countries this account asked for, fetched once. A failure leaves the list empty, not broken. */
export async function loadTaxVotes() {
  if (state.taxVotes !== null || taxVotesLoading || !state.user || state.user.isGuest) return;
  taxVotesLoading = true;
  try {
    state.taxVotes = (await api.taxVotes()).votes || [];
  } catch {
    state.taxVotes = [];
  } finally {
    taxVotesLoading = false;
    notify();
  }
}

/** Asks for a country, or takes it back -- shown at once, put right if the server says otherwise. */
export async function toggleTaxVote(country) {
  const mine = state.taxVotes || [];
  const on = !mine.includes(country);
  state.taxVotes = on ? [...mine, country] : mine.filter(key => key !== country);
  notify();
  try {
    state.taxVotes = (await api.setTaxVote(country, on)).votes || state.taxVotes;
  } catch (error) {
    state.taxVotes = mine;
    state.error = errorKey(error);
  }
  notify();
}

export function setJournalMode(mode) {
  state.journalMode = ['backtest', 'prop'].includes(mode) ? mode : 'live';
  state.backtestConfirm = false;
  persistUi();
  notify();
}

export function selectBacktest(id) {
  state.backtestId = id;
  state.backtestConfirm = false;
  persistUi();
  notify();
}

/**
 * A new session, opened straight away -- the next trade logged goes into it.
 * Takes its name, or everything it tests: instrument, months, capital and 1R.
 */
export async function addBacktest(session) {
  markGuestTouched();
  if (state.busy) return null;
  state.busy = true;
  state.error = null;
  notify();
  try {
    const row = await api.addBacktest(typeof session === 'string' ? { name: session } : session);
    state.backtests = [row, ...(state.backtests || [])];
    state.backtestId = row.id;
    persistUi();
    return row;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

/** A session's settings, corrected: the name, what it tests, the capital and 1R. */
export async function updateBacktest(id, patch) {
  markGuestTouched();
  try {
    const row = await api.updateBacktest(id, patch);
    state.backtests = (state.backtests || []).map(item => (item.id === id ? row : item));
    return row;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    notify();
  }
}

export function askDeleteBacktest(on) {
  state.backtestConfirm = Boolean(on);
  notify();
}

/** The session and its trades, gone together; put back if the server refuses. */
export async function removeBacktest(id) {
  const beforeSessions = state.backtests || [];
  const beforeTrades = state.trades || [];
  state.backtests = beforeSessions.filter(row => row.id !== id);
  state.trades = beforeTrades.filter(row => row.backtestId !== id);
  state.backtestConfirm = false;
  if (state.backtestId === id) state.backtestId = state.backtests[0]?.id || null;
  persistUi();
  notify();
  try {
    await api.deleteBacktest(id);
  } catch (error) {
    state.backtests = beforeSessions;
    state.trades = beforeTrades;
    state.error = errorKey(error);
    notify();
  }
}

// ----------------------------------------------------------- prop firms ---

export async function loadProp({ force = false } = {}) {
  if ((state.prop && !force) || state.propLoading) return state.prop;
  state.propLoading = true;
  notify();
  try {
    state.prop = await api.prop();
  } catch (error) {
    state.prop = state.prop || [];
    state.error = errorKey(error);
  } finally {
    state.propLoading = false;
    notify();
  }
  return state.prop;
}

/*
 * Every change comes back as the account as the server now has it. Costs and
 * payouts are mirrored into the ledger, so the ledger is fetched again too --
 * the month's expenses and income have just changed underneath it.
 */
async function propCall(run, { ledger = false } = {}) {
  if (state.busy) return null;
  state.busy = true;
  state.error = null;
  notify();
  try {
    const result = await run();
    if (ledger) refresh({ silent: true });
    return result;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

const replaceProp = row => {
  if (!row) return;
  const list = state.prop || [];
  state.prop = list.some(item => item.id === row.id)
    ? list.map(item => (item.id === row.id ? row : item))
    : [row, ...list];
};

/** A new account, with its entry fee logged in the same step when one is given. */
export async function addPropAccount(account, fee = null) {
  markGuestTouched();
  return propCall(async () => {
    let row = await api.addPropAccount(account);
    if (fee && fee.amount > 0) row = await api.addPropEvent(row.id, { ...fee, kind: 'fee' });
    replaceProp(row);
    return row;
  }, { ledger: Boolean(fee && fee.amount > 0) });
}

export async function updatePropAccount(id, patch) {
  markGuestTouched();
  return propCall(async () => {
    const row = await api.updatePropAccount(id, patch);
    replaceProp(row);
    return row;
  });
}

export function setPropRulesEdit(id) {
  state.propRulesEdit = id;
  notify();
}

export function setPropUi(patch) {
  state.propUi = { ...state.propUi, ...patch };
  notify();
}

/** The account's rules and its delete, with the drawdown type it has now. */
export function openPropSettings(account) {
  state.propRulesEdit = account.id;
  state.propConfirm = null;
  state.propUi = { ...state.propUi, entryFor: null, ddType: account.rules?.drawdownType || 'eod' };
  notify();
}

export function askDeletePropAccount(id) {
  state.propConfirm = id;
  notify();
}

export async function removePropAccount(id) {
  return propCall(async () => {
    await api.deletePropAccount(id);
    state.prop = (state.prop || []).filter(row => row.id !== id);
    state.propConfirm = null;
    return true;
  }, { ledger: true });
}

export async function addPropEvent(accountId, event) {
  markGuestTouched();
  return propCall(async () => {
    const row = await api.addPropEvent(accountId, event);
    replaceProp(row);
    return row;
  }, { ledger: event.toLedger !== false });
}

export async function removePropEvent(id) {
  return propCall(async () => {
    const row = await api.deletePropEvent(id);
    replaceProp(row);
    return row;
  }, { ledger: true });
}

/** The retry the failure state offers; deliberate, and the only way back in. */
export function retryTrades() {
  state.tradesFailed = false;
  return loadTrades({ force: true });
}

export async function addTrade(trade) {
  markGuestTouched();
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const row = await api.addTrade(trade);
    state.trades = [row, ...(state.trades || [])];
    if (row.currency && row.currency !== state.tradeCurrency) {
      state.tradeCurrency = row.currency;
      ui.tradeCurrency = row.currency;
      persistUi();
    }
    return row;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    state.busy = false;
    notify();
  }
}

export async function updateTrade(id, patch) {
  markGuestTouched();
  try {
    const row = await api.updateTrade(id, patch);
    state.trades = (state.trades || []).map(t => (t.id === id ? row : t));
    return row;
  } catch (error) {
    state.error = errorKey(error);
    return null;
  } finally {
    notify();
  }
}

export async function removeTrade(id) {
  const before = state.trades || [];
  const removed = before.find(t => t.id === id);
  // Optimistic, with the list put back if the server refuses.
  state.trades = before.filter(t => t.id !== id);
  /*
   * One click on × used to lose a trade for good -- the only delete in the app
   * with no way back. Undo writes it again from what was on screen.
   */
  if (removed) {
    rememberDeletion('trade', removed, row => {
      const { id: _id, createdAt: _created, updatedAt: _updated, ...fields } = row;
      /* Empty fields stay empty by being left out: the server reads null as "missing". */
      return addTrade(Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== null && value !== undefined)));
    });
  }
  notify();
  try {
    await api.deleteTrade(id);
  } catch (error) {
    state.trades = before;
    state.error = errorKey(error);
    notify();
  }
}


/**
 * A broker's export, read here and shown before anything is written: how many
 * trades, which days, what they came to. Nothing reaches the journal until
 * `confirmTradeImport`.
 */
export async function openTradeImport(file) {
  if (!file) return;
  state.error = null;
  state.errorDetail = null;
  let text;
  try {
    text = decodeStatement(new Uint8Array(await file.arrayBuffer()));
  } catch {
    state.error = 'import:unreadable';
    return notify();
  }
  const { source, trades, skipped } = readBrokerTrades(text);
  if (!source || !trades.length) {
    state.tradeImport = null;
    state.error = source ? 'tradeImport:empty' : 'tradeImport:unknown';
    return notify();
  }
  /* `keepTyped`: import a trade even where one typed in by hand looks the same. Off: those are left out. */
  state.tradeImport = { source, trades, skipped, name: file.name || 'Performance.csv', propAccountId: null, keepTyped: false, result: null };
  notify();
}

export function setTradeImport(patch) {
  if (!state.tradeImport) return;
  state.tradeImport = { ...state.tradeImport, ...patch };
  notify();
}

export function closeTradeImport() {
  state.tradeImport = null;
  notify();
}

export async function confirmTradeImport() {
  const draft = state.tradeImport;
  if (!draft || draft.result || state.busy) return;
  markGuestTouched();
  state.busy = true;
  state.error = null;
  state.errorDetail = null;
  notify();
  try {
    const twins = draft.keepTyped ? new Map() : typedTwins(draft.trades, state.trades || []);
    const sending = draft.trades.filter(trade => !twins.has(trade.importKey));
    let result = { added: [], updated: [], removed: [], duplicates: 0, overLimit: 0 };
    if (sending.length) {
      result = await api.importTrades({
        source: draft.source,
        propAccountId: draft.propAccountId || null,
        /* `openedAt` is for the preview; the server keeps the day. */
        trades: sending.map(({ openedAt: _opened, ...trade }) => trade)
      });
    }
    const { added, updated = [], removed = [], duplicates, overLimit } = result;
    const fresh = new Map(updated.map(row => [row.id, row]));
    const gone = new Set(removed);
    state.trades = [...added, ...(state.trades || []).filter(row => !gone.has(row.id)).map(row => fresh.get(row.id) || row)];
    state.tradeImport = { ...draft, result: { added: added.length, updated: updated.length, typed: twins.size, duplicates, overLimit } };
  } catch (error) {
    state.error = errorKey(error);
  } finally {
    state.busy = false;
    notify();
  }
}

/**
 * Moves a fill-up or a service to the day it actually happened.
 *
 * Until now these could only be deleted and retyped: the forms stamped today's
 * date and nothing could correct it afterwards. Both rows are mirrored into the
 * month's expenses, so the server moves that line too and the snapshot is
 * refetched rather than guessed at locally.
 */
export function setFuelDate(id, date) {
  mutate(
    { type: 'updateFuel', targetId: id, payload: { date } },
    data => {
      const row = data.fuel.find(r => r.id === id);
      if (row) row.date = date;
    }
  );
}

export function setCarExpenseDate(id, date) {
  mutate(
    { type: 'updateCarExpense', targetId: id, payload: { date } },
    data => {
      const row = data.carEx.find(r => r.id === id);
      if (row) row.date = date;
    }
  );
}


// ------------------------------------------------------------ advertising ---

/**
 * Whether this account sees advertising, asked once per session.
 *
 * The server decides — the client does not work it out from a plan string it
 * could be wrong about, and an instance with no publisher id configured simply
 * answers no, so nothing is ever requested from an ad network.
 */
let adsLoading = false;

export async function loadAds() {
  if (state.ads) return state.ads;
  /*
   * In flight already. Without this the slot asks again on every repaint for as
   * long as the answer takes: measured at six requests for one answer on a slow
   * connection, and it scales with how busy the page is.
   */
  if (adsLoading) return null;

  adsLoading = true;
  try {
    state.ads = await api.adsConfig();
  } catch {
    /*
     * Silent, and off. A failed request here must not put an error in front of
     * somebody looking at their own accounts — the worst outcome of not knowing
     * is that no advertising is shown, which is the safe direction.
     */
    state.ads = { show: false };
  } finally {
    adsLoading = false;
  }
  notify();
  return state.ads;
}

/**
 * Records the answer to the consent prompt.
 *
 * 'no' does not mean "no ads" — it means non-personalised ones, which is what
 * the frame requests by default. Saying so plainly in the prompt matters more
 * than the wording of any policy page.
 */
/** The frame reported that it loaded, so the slot is real and stays. */
export function adFrameLoaded() {
  if (state.adFrame === 'loaded') return;
  state.adFrame = 'loaded';
  notify();
}

/** Nothing arrived in time; the slot removes itself rather than sit empty. */
export function adFrameBlocked() {
  if (state.adFrame !== 'waiting') return;
  state.adFrame = 'blocked';
  notify();
}

export function setAdConsent(answer) {
  /*
   * Null puts the question back, which is what the "change this" link does.
   * A consent choice you cannot revisit is not a choice, and it has to be as
   * easy to withdraw as it was to give.
   */
  state.adConsent = answer === 'yes' ? 'yes' : (answer === 'no' ? 'no' : null);
  // A fresh answer swaps the frame's src, so it deserves a fresh verdict.
  state.adFrame = 'waiting';
  if (!sandbox) try {
    if (state.adConsent) window.localStorage.setItem(AD_CONSENT_KEY, state.adConsent);
    else window.localStorage.removeItem(AD_CONSENT_KEY);
  } catch {
    // Storage disabled: the choice holds for this session only, and the
    // default it falls back to is the non-personalised one.
  }
  notify();
}


// ------------------------------------------------------ inflation rates ----

/**
 * The annual rate used to value cash positions.
 *
 * Queued like a rate change, and like one it re-values more than the row it
 * touches: every cash holding in that currency reports a different erosion
 * afterwards, so the snapshot is recomputed rather than patched.
 */
export function setInflation(currency, rate) {
  flashSaved();
  const code = String(currency || '').toUpperCase();
  mutate(
    { type: 'setInflation', payload: { currency: code, rate } },
    data => {
      data.inflation = data.inflation || [];
      const row = data.inflation.find(r => r.currency === code);
      if (row) row.rate = rate;
      else data.inflation.push({ currency: code, rate });
    }
  );
}

export function removeInflation(currency) {
  flashSaved();
  const code = String(currency || '').toUpperCase();
  mutate(
    { type: 'deleteInflation', targetId: code },
    data => {
      data.inflation = (data.inflation || []).filter(r => r.currency !== code);
    }
  );
}
